import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Github, Instagram, Facebook, Youtube, Music, Globe, Link as LinkIcon, Mail, Phone, Calendar, Pencil, Trash2, RefreshCw, RotateCw, MessageCircle, DollarSign, Home, Video, Send, Headphones, Camera, ShoppingBag, FileText, Building2, Briefcase, CreditCard, MapPin, Check, Copy, Plus, Search } from "lucide-react";
import { useToast } from "./Toast";
import CardRenderer, { CardRendererCard, CardRendererLink } from "./CardRenderer";
import IndustryPicker, { IndustryPickerResult } from "./IndustryPicker";
import {
  PresetLink,
  stripCustomFieldsForFree,
} from "../utils/industryPresets";
import Button from "./ui/Button";
import WalletSection from "./WalletSection";
import { sanitizeHandle } from "./HeroHandlePicker";
import {
  fetchUserByEmail,
  syncUser,
  updateUser,
  fetchCards,
  createCard,
  updateCard,
  fetchCardLinks,
  createCardLink,
  updateCardLink,
  deleteCardLink,
  fetchVisitStats,
  fetchLinkClicks,
  fetchLinkPreview,
  User,
  UserCreate,
  Card,
  CardLink,
  VisitStats,
  ContactFormConfig,
  ContactFormFieldConfig,
  ContactFormFieldType,
  LinkType,
} from "../utils/api";
import { validateLogoFile, styleForDimensions, routeLogo, rotateImage90, isUnderLogoCap } from "../utils/logoUpload";

const EMPTY_FORM_CONFIG: ContactFormConfig = {
  buttonLabel: 'Contact me',
  description: '',
  customFields: [],
};

const MAX_CUSTOM_FIELDS = 4;
const MAX_FIELD_OPTIONS = 12;
const FIELD_TYPE_LABELS: Record<ContactFormFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  phone: 'Phone',
  select: 'Single-select',
  multiselect: 'Multi-select',
  yes_no: 'Yes / No',
  file_upload: 'Resume / File upload',
};

function makeFieldId(): string {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

const LINK_CATEGORIES = ["All", "Calendar", "Contact", "Social Media", "Business", "Content", "Real Estate", "Payments", "Music"];

// The five presets surfaced in the picker's empty state. The catalog is one
// click away ("Show more apps"); these are the cards a brand-new user almost
// always reaches for first. Order matters — left-to-right in the grid.
const POPULAR_PRESET_TITLES = ["Email", "Phone", "LinkedIn", "Calendly", "Custom Form"];

// Theme tiles surfaced in the Edit my Card picker. Each tile previews the
// theme's light and dark variants side-by-side so owners see how their card
// reads in both modes (see THEME_STYLES below for the rendered classes).
type ThemeOption = {
  id: string;
  name: string;
  light: string;
  lightText: string;
  dark: string;
  darkText: string;
  proOnly: boolean;
};
const THEME_OPTIONS: ThemeOption[] = [
  { id: 'default',    name: 'Classic',    light: 'bg-gradient-to-br from-coffee-50 to-cream-100',   lightText: 'text-coffee-900',  dark: 'bg-gradient-to-br from-coffee-900 to-coffee-950',   darkText: 'text-cream-50',   proOnly: false },
  { id: 'minimal',    name: 'Minimal',    light: 'bg-gray-100',                                     lightText: 'text-gray-900',    dark: 'bg-zinc-900',                                       darkText: 'text-gray-50',    proOnly: false },
  { id: 'bold',       name: 'Bold',       light: 'bg-gradient-to-br from-coffee-300 to-coffee-400', lightText: 'text-coffee-950',  dark: 'bg-gradient-to-br from-coffee-900 to-coffee-950',   darkText: 'text-white',      proOnly: false },
  { id: 'ocean',      name: 'Ocean',      light: 'bg-gradient-to-br from-blue-100 to-cyan-100',     lightText: 'text-blue-900',    dark: 'bg-gradient-to-br from-blue-950 to-slate-950',      darkText: 'text-blue-50',    proOnly: false },
  { id: 'forest',     name: 'Forest',     light: 'bg-gradient-to-br from-green-100 to-emerald-100', lightText: 'text-green-900',   dark: 'bg-gradient-to-br from-emerald-950 to-green-950',   darkText: 'text-emerald-50', proOnly: false },
  { id: 'sunset',     name: 'Sunset',     light: 'bg-gradient-to-br from-orange-100 to-rose-100',   lightText: 'text-orange-900',  dark: 'bg-gradient-to-br from-rose-950 to-orange-950',     darkText: 'text-rose-50',    proOnly: false },
  { id: 'midnight',   name: 'Midnight',   light: 'bg-gradient-to-br from-sky-50 to-blue-100',       lightText: 'text-slate-900',   dark: 'bg-gradient-to-br from-slate-950 to-blue-950',      darkText: 'text-white',      proOnly: true },
  { id: 'ruby',       name: 'Ruby',       light: 'bg-gradient-to-br from-rose-100 to-red-100',      lightText: 'text-red-900',     dark: 'bg-gradient-to-br from-red-950 to-rose-950',        darkText: 'text-white',      proOnly: true },
  { id: 'emerald',    name: 'Emerald',    light: 'bg-gradient-to-br from-emerald-100 to-green-100', lightText: 'text-emerald-900', dark: 'bg-gradient-to-br from-emerald-950 to-green-950',   darkText: 'text-white',      proOnly: true },
  { id: 'lavender',   name: 'Lavender',   light: 'bg-gradient-to-br from-purple-100 to-violet-200', lightText: 'text-purple-900',  dark: 'bg-gradient-to-br from-purple-950 to-violet-950',   darkText: 'text-violet-50',  proOnly: true },
  { id: 'monochrome', name: 'Monochrome', light: 'bg-zinc-50',                                      lightText: 'text-zinc-900',    dark: 'bg-zinc-950',                                       darkText: 'text-white',      proOnly: true },
];

const SocialIcon: React.FC<{ name: string; className?: string }> = ({ name, className = "w-5 h-5" }) => {
  const icons: Record<string, React.ReactNode> = {
    linkedin: <Linkedin className={className} />,
    twitter: <Twitter className={className} />,
    github: <Github className={className} />,
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    youtube: <Youtube className={className} />,
    tiktok: <Music className={className} />,
    globe: <Globe className={className} />,
    link: <LinkIcon className={className} />,
    mail: <Mail className={className} />,
    phone: <Phone className={className} />,
    calendar: <Calendar className={className} />,
    message: <MessageCircle className={className} />,
    dollar: <DollarSign className={className} />,
    home: <Home className={className} />,
    video: <Video className={className} />,
    send: <Send className={className} />,
    headphones: <Headphones className={className} />,
    camera: <Camera className={className} />,
    store: <ShoppingBag className={className} />,
    file: <FileText className={className} />,
    building: <Building2 className={className} />,
    briefcase: <Briefcase className={className} />,
    credit: <CreditCard className={className} />,
    mappin: <MapPin className={className} />,
  };
  return <>{icons[name] || <LinkIcon className={className} />}</>;
};

type LinkPreset = { icon: string; title: string; url: string; category: string; linkType?: LinkType };

const LINK_PRESETS: LinkPreset[] = [
  // Calendar
  { icon: "calendar", title: "Calendly", url: "https://calendly.com/", category: "Calendar" },
  { icon: "calendar", title: "Google Calendar", url: "https://calendar.google.com/", category: "Calendar" },
  { icon: "calendar", title: "Cal.com", url: "https://cal.com/", category: "Calendar" },
  { icon: "calendar", title: "iCal Link", url: "", category: "Calendar" },
  { icon: "calendar", title: "Microsoft Bookings", url: "https://outlook.office.com/bookwithme/", category: "Calendar" },

  // Contact
  { icon: "mail", title: "Email", url: "mailto:", category: "Contact" },
  { icon: "phone", title: "Phone", url: "tel:", category: "Contact" },
  { icon: "message", title: "WhatsApp", url: "https://wa.me/", category: "Contact" },
  { icon: "file", title: "Custom Form", url: "", category: "Contact", linkType: "contact_form" as LinkType },

  // Social Media
  { icon: "instagram", title: "Instagram", url: "https://instagram.com/", category: "Social Media" },
  { icon: "linkedin", title: "LinkedIn", url: "https://linkedin.com/in/", category: "Social Media" },
  { icon: "facebook", title: "Facebook", url: "https://facebook.com/", category: "Social Media" },
  { icon: "youtube", title: "YouTube", url: "https://youtube.com/@", category: "Social Media" },
  { icon: "twitter", title: "X (Twitter)", url: "https://x.com/", category: "Social Media" },
  { icon: "tiktok", title: "TikTok", url: "https://tiktok.com/@", category: "Social Media" },
  { icon: "camera", title: "Snapchat", url: "https://snapchat.com/add/", category: "Social Media" },
  { icon: "video", title: "Twitch", url: "https://twitch.tv/", category: "Social Media" },
  { icon: "mappin", title: "Pinterest", url: "https://pinterest.com/", category: "Social Media" },
  { icon: "message", title: "Discord", url: "https://discord.gg/", category: "Social Media" },
  { icon: "send", title: "Telegram", url: "https://t.me/", category: "Social Media" },
  { icon: "message", title: "Threads", url: "https://threads.net/@", category: "Social Media" },

  // Business
  { icon: "globe", title: "Website", url: "https://", category: "Business" },
  { icon: "briefcase", title: "Portfolio", url: "https://", category: "Business" },
  { icon: "github", title: "GitHub", url: "https://github.com/", category: "Business" },
  { icon: "store", title: "App Store", url: "https://apps.apple.com/", category: "Business" },
  { icon: "store", title: "Google Play", url: "https://play.google.com/store/apps/", category: "Business" },
  { icon: "briefcase", title: "Behance", url: "https://behance.net/", category: "Business" },
  { icon: "briefcase", title: "Dribbble", url: "https://dribbble.com/", category: "Business" },

  // Content
  { icon: "file", title: "Resume / CV", url: "", category: "Content" },
  { icon: "file", title: "Text Section", url: "", category: "Content" },
  { icon: "globe", title: "Custom Link", url: "https://", category: "Content" },
  { icon: "file", title: "Medium", url: "https://medium.com/@", category: "Content" },
  { icon: "file", title: "Substack", url: "https://substack.com/@", category: "Content" },
  { icon: "file", title: "Blog", url: "https://", category: "Content" },

  // Real Estate
  { icon: "home", title: "Zillow", url: "https://zillow.com/profile/", category: "Real Estate" },
  { icon: "home", title: "Realtor.com", url: "https://realtor.com/realestateagents/", category: "Real Estate" },
  { icon: "home", title: "Redfin", url: "https://redfin.com/real-estate-agents/", category: "Real Estate" },
  { icon: "building", title: "Compass", url: "https://compass.com/agents/", category: "Real Estate" },
  { icon: "mappin", title: "Open House", url: "https://", category: "Real Estate" },
  { icon: "video", title: "Virtual Tour", url: "https://", category: "Real Estate" },

  // Payments
  { icon: "dollar", title: "Cash App", url: "https://cash.app/$", category: "Payments" },
  { icon: "dollar", title: "Venmo", url: "https://venmo.com/", category: "Payments" },
  { icon: "credit", title: "PayPal", url: "https://paypal.me/", category: "Payments" },
  { icon: "dollar", title: "Zelle", url: "", category: "Payments" },

  // Music
  { icon: "headphones", title: "Spotify", url: "https://open.spotify.com/artist/", category: "Music" },
  { icon: "headphones", title: "Apple Music", url: "https://music.apple.com/artist/", category: "Music" },
  { icon: "headphones", title: "SoundCloud", url: "https://soundcloud.com/", category: "Music" },
  { icon: "tiktok", title: "Bandcamp", url: "https://bandcamp.com/", category: "Music" },
];

const BADGE_STYLES = {
    gold: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-900',
    platinum: 'bg-gradient-to-r from-gray-300 to-gray-100 text-gray-700',
    dark: 'bg-gradient-to-r from-coffee-800 to-coffee-900 text-coffee-100',
};

// Mirrors CardRenderer's themeStyles — class strings carry both light + dark
// variants so the editor's local preview adapts to the global dark-mode toggle
// the same way the live public card does.
const THEME_STYLES: Record<string, { bg: string; text: string; accent: string; card: string; subtext: string }> = {
    default:    { bg: 'bg-gradient-to-br from-coffee-50 to-cream-100 dark:from-coffee-900 dark:to-coffee-950',     text: 'text-coffee-900 dark:text-cream-50',   accent: 'bg-coffee-600 dark:bg-coffee-500',   card: 'bg-white dark:bg-coffee-800',         subtext: 'text-coffee-600 dark:text-coffee-200' },
    minimal:    { bg: 'bg-gray-100 dark:bg-zinc-900',                                                              text: 'text-gray-900 dark:text-gray-50',      accent: 'bg-gray-800 dark:bg-gray-300',       card: 'bg-white dark:bg-zinc-800',           subtext: 'text-gray-600 dark:text-gray-400' },
    bold:       { bg: 'bg-gradient-to-br from-coffee-300 to-coffee-400 dark:from-coffee-900 dark:to-coffee-950',   text: 'text-coffee-950 dark:text-white',      accent: 'bg-coffee-800 dark:bg-coffee-500',   card: 'bg-coffee-100 dark:bg-coffee-700',    subtext: 'text-coffee-800 dark:text-coffee-200' },
    ocean:      { bg: 'bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-950 dark:to-slate-950',          text: 'text-blue-900 dark:text-blue-50',      accent: 'bg-blue-600 dark:bg-blue-500',       card: 'bg-white dark:bg-blue-900/80',        subtext: 'text-blue-600 dark:text-blue-200' },
    forest:     { bg: 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-emerald-950 dark:to-green-950',   text: 'text-green-900 dark:text-emerald-50',  accent: 'bg-green-600 dark:bg-emerald-500',   card: 'bg-white dark:bg-emerald-900/80',     subtext: 'text-green-600 dark:text-emerald-200' },
    sunset:     { bg: 'bg-gradient-to-br from-orange-100 to-rose-100 dark:from-rose-950 dark:to-orange-950',       text: 'text-orange-900 dark:text-rose-50',    accent: 'bg-orange-600 dark:bg-rose-500',     card: 'bg-white dark:bg-rose-900/80',        subtext: 'text-orange-600 dark:text-rose-200' },
    midnight:   { bg: 'bg-gradient-to-br from-sky-50 to-blue-100 dark:from-slate-950 dark:to-blue-950',            text: 'text-slate-900 dark:text-white',       accent: 'bg-blue-700 dark:bg-blue-500',       card: 'bg-white dark:bg-slate-800/80',       subtext: 'text-slate-600 dark:text-blue-200' },
    ruby:       { bg: 'bg-gradient-to-br from-rose-100 to-red-100 dark:from-red-950 dark:to-rose-950',             text: 'text-red-900 dark:text-white',         accent: 'bg-rose-600 dark:bg-rose-500',       card: 'bg-white dark:bg-red-800/80',         subtext: 'text-rose-700 dark:text-rose-200' },
    emerald:    { bg: 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-950 dark:to-green-950',   text: 'text-emerald-900 dark:text-white',     accent: 'bg-emerald-600 dark:bg-emerald-500', card: 'bg-white dark:bg-emerald-800/80',     subtext: 'text-emerald-700 dark:text-emerald-200' },
    lavender:   { bg: 'bg-gradient-to-br from-purple-100 to-violet-200 dark:from-purple-950 dark:to-violet-950',   text: 'text-purple-900 dark:text-violet-50',  accent: 'bg-violet-600 dark:bg-violet-500',   card: 'bg-white dark:bg-purple-900/80',      subtext: 'text-purple-600 dark:text-violet-200' },
    monochrome: { bg: 'bg-zinc-50 dark:bg-zinc-950',                                                               text: 'text-zinc-900 dark:text-white',        accent: 'bg-zinc-700 dark:bg-zinc-400',       card: 'bg-white dark:bg-zinc-800',           subtext: 'text-zinc-600 dark:text-zinc-400' },
};

/* ------------------------------------------------------------------ */
/*  Loading skeleton                                                   */
/* ------------------------------------------------------------------ */
const SkeletonBlock: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse bg-coffee-200 rounded-lg ${className}`} />
);

const LoadingSkeleton: React.FC = () => (
  <div>
    <div className="max-w-4xl mx-auto space-y-8">
      <SkeletonBlock className="h-64" />
      <SkeletonBlock className="h-40" />
      <SkeletonBlock className="h-96" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonBlock key={i} className="h-24" />
        ))}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
const MAX_LINKS_FREE = 5;
const MAX_LINKS_PAID = 10;
const MAX_CARDS_FREE = 1;
const MAX_CARDS_PAID = 3;

// ─── Contact-form config editor ────────────────────────────────────────────
//
// Controlled UI for the buttonLabel / description / customFields portion of a
// ContactFormConfig. Used by both the Add Link form and the Edit Link form.
// Free users see the base 3 fields locked and can't add custom fields; Pro
// users get up to 4 custom fields with type + required + select-options.

interface ContactFormConfigEditorProps {
  value: ContactFormConfig;
  onChange: (next: ContactFormConfig) => void;
  isPaidUser: boolean;
}

/** Trim, drop blank lines, cap — the option shape the live form renders from. */
const parseFieldOptions = (raw: string): string[] =>
  raw.split('\n').map(o => o.trim()).filter(Boolean).slice(0, MAX_FIELD_OPTIONS);

interface FieldOptionsInputProps {
  id: string;
  options?: string[];
  onOptionsChange: (options: string[]) => void;
}

// Options editor for a select / multiselect field.
//
// The raw text lives in local state so the box shows exactly what was typed —
// blank lines and a trailing newline included. Deriving the textarea's `value`
// from the trimmed options array instead (as this once did) stripped the
// newline Enter had just created before the next render, so Enter looked dead.
// The trimmed and capped array is still pushed up on every keystroke, leaving
// what gets saved identical to before.
const FieldOptionsInput: React.FC<FieldOptionsInputProps> = ({ id, options, onOptionsChange }) => {
  const [raw, setRaw] = useState(() => (options || []).join('\n'));

  return (
    <div>
      <label htmlFor={id} className="block text-[11px] font-medium text-coffee-600 mb-1">Options (one per line)</label>
      <textarea
        id={id}
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          onOptionsChange(parseFieldOptions(e.target.value));
        }}
        className="w-full px-3 py-2 border border-coffee-200 rounded text-sm focus:ring-2 focus:ring-coffee-500"
        rows={3}
        placeholder="Option 1&#10;Option 2&#10;Option 3"
      />
    </div>
  );
};

export const ContactFormConfigEditor: React.FC<ContactFormConfigEditorProps> = ({ value, onChange, isPaidUser }) => {
  const customFields = value.customFields || [];
  const limitReached = customFields.length >= MAX_CUSTOM_FIELDS;

  const updateField = (idx: number, patch: Partial<ContactFormFieldConfig>) => {
    const next = customFields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange({ ...value, customFields: next });
  };

  const removeField = (idx: number) => {
    onChange({ ...value, customFields: customFields.filter((_, i) => i !== idx) });
  };

  const addField = () => {
    if (!isPaidUser || limitReached) return;
    onChange({
      ...value,
      customFields: [
        ...customFields,
        { id: makeFieldId(), label: '', type: 'short_text', required: false },
      ],
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-coffee-700 mb-2">Button label *</label>
        <input
          type="text"
          value={value.buttonLabel || ''}
          onChange={(e) => onChange({ ...value, buttonLabel: e.target.value })}
          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500"
          placeholder="Contact me"
          maxLength={80}
          required
        />
        <p className="mt-1 text-xs text-coffee-500">The clickable label visitors see on your card. They'll see this in their lead notification too.</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-coffee-700 mb-2">Description (optional)</label>
        <input
          type="text"
          value={value.description || ''}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500"
          placeholder="Drop your contact and I'll reach out within a day."
          maxLength={280}
        />
      </div>

      <div className="border-t border-coffee-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-medium text-coffee-900">Form fields</p>
            <p className="text-xs text-coffee-500">Visitors always answer Name · Phone · Email{customFields.length > 0 ? ` plus the ${customFields.length} custom field${customFields.length === 1 ? '' : 's'} below` : ''}.</p>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-coffee-500">
            {isPaidUser ? `${customFields.length} of ${MAX_CUSTOM_FIELDS}` : 'Base only'}
          </span>
        </div>

        {/* Locked base fields, for visibility */}
        <ul className="space-y-2 mb-3">
          {['Name', 'Phone', 'Email'].map((label) => (
            <li key={label} className="flex items-center gap-3 px-3 py-2 bg-coffee-100/40 border border-coffee-200 rounded">
              <span className="text-sm text-coffee-900 font-medium">{label}</span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-coffee-500">REQUIRED</span>
            </li>
          ))}
        </ul>

        {/* Custom fields list */}
        {customFields.length > 0 && (
          <ul className="space-y-3">
            {customFields.map((field, idx) => (
              <li key={field.id} className="bg-white border border-coffee-200 rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-6">
                    <label className="block text-[11px] font-medium text-coffee-600 mb-1">Question label</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      className="w-full px-3 py-2 border border-coffee-200 rounded text-sm focus:ring-2 focus:ring-coffee-500"
                      placeholder="e.g., Company size"
                      maxLength={120}
                      required
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label htmlFor={`field-type-${field.id}`} className="block text-[11px] font-medium text-coffee-600 mb-1">Type</label>
                    <select
                      id={`field-type-${field.id}`}
                      value={field.type}
                      onChange={(e) => updateField(idx, { type: e.target.value as ContactFormFieldType })}
                      className="w-full px-3 py-2 border border-coffee-200 rounded text-sm focus:ring-2 focus:ring-coffee-500"
                    >
                      {(Object.keys(FIELD_TYPE_LABELS) as ContactFormFieldType[]).map(t => {
                        // D-005: one attachment per form — a lead row stores a
                        // single file. Compared by index so the field that
                        // already IS the file field keeps its own option
                        // selectable; otherwise it could never be edited.
                        const takenElsewhere = t === 'file_upload'
                          && customFields.some((f, i) => i !== idx && f.type === 'file_upload');
                        return (
                          <option key={t} value={t} disabled={takenElsewhere}>
                            {FIELD_TYPE_LABELS[t]}{takenElsewhere ? ' — already added' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {field.type === 'file_upload' && (
                      <p className="mt-1 text-[11px] text-coffee-500">
                        Visitors can attach one PDF or DOCX, up to 5MB.
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2 flex items-end gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-coffee-700 select-none cursor-pointer pb-2">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => removeField(idx)}
                      className="ml-auto text-coffee-500 hover:text-danger-500 pb-2"
                      aria-label="Remove field"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {(field.type === 'select' || field.type === 'multiselect') && (
                  <FieldOptionsInput
                    id={`field-options-${field.id}`}
                    options={field.options}
                    onOptionsChange={(options) => updateField(idx, { options })}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Add custom field button — Pro gated */}
        <button
          type="button"
          onClick={addField}
          disabled={!isPaidUser || limitReached}
          className={`mt-3 w-full h-10 border border-dashed rounded text-sm inline-flex items-center justify-center gap-2 transition-colors ${
            isPaidUser && !limitReached
              ? 'border-coffee-400 text-coffee-700 hover:border-coffee-700 hover:bg-coffee-50'
              : 'border-coffee-200 text-coffee-400 cursor-not-allowed'
          }`}
        >
          {limitReached ? (
            `Limit reached · ${MAX_CUSTOM_FIELDS} of ${MAX_CUSTOM_FIELDS} custom fields`
          ) : (
            <>
              + Add custom field
              {!isPaidUser && (
                <span className="ml-2 px-2 py-0.5 bg-coffee-900 text-cream-50 rounded text-[10px] font-mono uppercase tracking-[0.18em]">Pro</span>
              )}
            </>
          )}
        </button>
        {!isPaidUser && (
          <p className="mt-2 text-xs text-coffee-500">
            Pro adds up to {MAX_CUSTOM_FIELDS} custom questions per contact-form link.{' '}
            <Link to="/pricing" className="underline underline-offset-2 hover:text-coffee-900">Upgrade for $4.99/mo</Link>.
          </p>
        )}
      </div>
    </div>
  );
};

const MyCard: React.FC = () => {
  const { user } = useUser();
  const { addToast } = useToast();

  // Data state
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const card = selectedCardIndex >= 0 ? (cards[selectedCardIndex] || null) : null;
  const [links, setLinks] = useState<CardLink[]>([]);
  const [visitStats, setVisitStats] = useState<VisitStats | null>(null);
  const [linkClicks, setLinkClicks] = useState<Record<string, number>>({});

  const isPaidUser = dbUser?.subscription === 'pro' || dbUser?.subscription === 'paid';
  const [qrFgColor, setQrFgColor] = useState('000000');
  const [qrBgColor, setQrBgColor] = useState('ffffff');
  const maxLinks = isPaidUser ? MAX_LINKS_PAID : MAX_LINKS_FREE;
  const maxCards = isPaidUser ? MAX_CARDS_PAID : MAX_CARDS_FREE;

  // Set page title
  useEffect(() => {
    document.title = "My Card - WhoBrew";
  }, []);

  // Handle checkout success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      addToast('Subscription activated! Welcome to Pro.', 'success');
      // Clean up the URL
      window.history.replaceState({}, '', '/my-card');
      // Reload user data to get updated subscription
      loadData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab state is now derived from URL via useLocation (see above)

  // UI state
  const [loading, setLoading] = useState(true);
  const [proBannerDismissed, setProBannerDismissed] = useState(() => sessionStorage.getItem('pro_banner_dismissed') === 'true');
  const [editing, setEditing] = useState(false);
  // Mobile-only: which tab is active in the Edit my Card view. Desktop shows
  // all three panes (theme rail · form · live preview) simultaneously, so this
  // state is ignored above the lg breakpoint.
  const [editTab, setEditTab] = useState<'content' | 'theme' | 'display'>('content');
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showContactBox, setShowContactBox] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [vcfIncludeEmail, setVcfIncludeEmail] = useState(true);
  const [vcfIncludePhone, setVcfIncludePhone] = useState(true);
  const [vcfIncludeAddress, setVcfIncludeAddress] = useState(true);
  const [vcfIncludeWebsite, setVcfIncludeWebsite] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showAddLinkForm, setShowAddLinkForm] = useState(false);
  const [editingLink, setEditingLink] = useState<CardLink | null>(null);
  // Inline rename state for the My Card header. Click the card name to enter
  // an input; Enter or blur commits via updateCard; Escape cancels.
  const [renamingCardId, setRenamingCardId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  // Industry-preset onboarding. When a new user with no card and no industry
  // arrives, the picker is shown; on completion we stash the preset links here
  // and let the existing card-create flow run. After createCard succeeds, an
  // effect serially creates each preset link against the new card.
  const [pendingPresetLinks, setPendingPresetLinks] = useState<PresetLink[] | null>(null);

  // Account-level handle editing lives on /profile. Removed from My Card
  // surfaces so the URL doesn't appear on card-shaped UI.

  // Links section visibility — collapsed by default to keep the My Card view
  // focused on the card preview. Click "Edit my Links" in the V-B header to
  // expand it; click "Done" inside the panel to collapse it.
  const [showLinksEditor, setShowLinksEditor] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  // Catalog is hidden by default; the picker opens with just five popular tiles
  // + the search field. "Show more apps" expands the full grouped catalog.
  const [showAllPresets, setShowAllPresets] = useState(false);
  const linkSearchRef = useRef<HTMLInputElement | null>(null);

  // ⌘K / Ctrl+K focuses the link picker's search field while the Edit Links
  // panel is open. Mirrors the affordance hinted at by the kbd hint in the
  // search bar; matches the Command Palette UX in the rest of the app.
  useEffect(() => {
    if (!showLinksEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        linkSearchRef.current?.focus();
        linkSearchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLinksEditor]);

  // ─── Link-picker derived data ────────────────────────────────────────────
  //
  // The picker (command-palette layout, see /Product Proposals/12-*) needs
  // three pieces: the curated "Popular" tiles for the empty state, the
  // filtered match list for the active query, and a single helper that opens
  // the Add Link form with a preset pre-applied. Memoized so typing doesn't
  // re-walk LINK_PRESETS on every keystroke.
  const popularPresets = useMemo(
    () => POPULAR_PRESET_TITLES
      .map((title) => LINK_PRESETS.find((p) => p.title === title))
      .filter((p): p is LinkPreset => !!p),
    []
  );
  const filteredPresets = useMemo(() => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return [] as LinkPreset[];
    return LINK_PRESETS.filter((p) => p.title.toLowerCase().includes(q));
  }, [linkSearch]);
  const addPresetLink = (preset: LinkPreset) => {
    setShowAddLinkForm(true);
    setNewLink({
      title: preset.title,
      url: preset.url,
      description: "",
      icon: preset.icon,
      category: preset.category,
      linkType: preset.linkType || 'url',
      formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
    });
    setLinkSearch("");
  };
  // copied state removed — click-to-copy now uses toast
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const currentTheme = THEME_STYLES[selectedTheme] || THEME_STYLES.default;
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [wideLogo, setWideLogo] = useState<string | null>(null);
  const [squareLogo, setSquareLogo] = useState<string | null>(null);
  const [logoStyle, setLogoStyle] = useState<'wide' | 'square'>('square');
  const [showProBadge, setShowProBadge] = useState(true);
  const [badgeStyle, setBadgeStyle] = useState<'gold' | 'platinum' | 'dark'>('gold');
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<string | null>(null);
  const [resumeFilename, setResumeFilename] = useState<string>('');
  const [resumeType, setResumeType] = useState<string>('');

  // Card / profile form state
  const [cardForm, setCardForm] = useState({
    name: "",
    position: "",
    company: "",
    email: "",
    phoneNumber: "",
    officeAddress: "",
    bio: "",
    username: "",
  });

  // New link form state
  const [newLink, setNewLink] = useState<{
    title: string;
    url: string;
    description: string;
    icon: string;
    category: string;
    linkType: LinkType;
    formConfig: ContactFormConfig;
  }>({
    title: "",
    url: "",
    description: "",
    icon: "link",
    category: "Other",
    linkType: 'url',
    formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
  });

  /* ---------- helpers ---------- */

  // BL-008: the public URL is the user's ACCOUNT HANDLE (users.username), not a
  // per-card slug. `/card/{handle}` always serves whichever card is active.
  const handle = dbUser?.username || "";
  const hasHandle = handle.length > 0;

  const cardUrl = card && hasHandle
    ? `${window.location.origin}/card/${handle}`
    : "";

  const cardUrlQr = card && hasHandle
    ? `${window.location.origin}/card/${handle}?ref=qr`
    : "";

  const qrUrl = card && hasHandle
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(cardUrlQr)}&color=${qrFgColor}&bgcolor=${qrBgColor}`
    : "";

  /* ---------- load everything on mount ---------- */

  const loadData = useCallback(async () => {
    if (!user) return;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;

    setLoading(true);
    try {
      // 1. Ensure the user exists in the backend
      let fetchedUser: User;
      try {
        fetchedUser = await fetchUserByEmail(email);
      } catch {
        const userData: UserCreate = {
          username: email.split("@")[0],
          email,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
        };
        fetchedUser = await syncUser(userData);
      }
      setDbUser(fetchedUser);

      // 2. Try to find existing cards for this user
      let fetchedCards: Card[] = [];
      try {
        const userCards = await fetchCards(fetchedUser.id);
        if (userCards && userCards.length > 0) {
          fetchedCards = userCards;
        }
      } catch {
        // no cards yet -- that's fine
      }

      setCards(fetchedCards);

      if (fetchedCards.length > 0) {
        const fetchedCard = fetchedCards[0];
        setSelectedCardIndex(0);
        setCardForm({
          name: fetchedCard.name || "",
          position: fetchedCard.position || "",
          company: fetchedCard.company || "",
          email: fetchedCard.email || "",
          phoneNumber: fetchedCard.phoneNumber || "",
          officeAddress: fetchedUser.shippingAddress || "",
          bio: (fetchedUser.bio as string) || "",
          username: fetchedCard.username || "",
        });
        setWideLogo(fetchedCard.wideLogo || null);
        setSquareLogo(fetchedCard.squareLogo || null);
        setLogoStyle((fetchedCard.logoStyle as any) || 'square');
        setShowProBadge(fetchedCard.showProBadge !== false);
        setBadgeStyle((fetchedCard.badgeStyle as any) || 'gold');
        setShowEmail(fetchedCard.showEmail !== false);
        setShowPhone(fetchedCard.showPhone !== false);
        setShowContactBox(fetchedCard.showContactBox !== false);
        setShowAddress(fetchedCard.showAddress !== false);
        setVcfIncludeEmail(fetchedCard.vcfIncludeEmail !== false);
        setVcfIncludePhone(fetchedCard.vcfIncludePhone !== false);
        setVcfIncludeAddress(fetchedCard.vcfIncludeAddress !== false);
        setVcfIncludeWebsite(fetchedCard.vcfIncludeWebsite !== false);
        setProfilePhoto(fetchedCard.profilePhoto || null);
        setResumeFilename(fetchedCard.resumeFilename || '');
        setResumeType(fetchedCard.resumeType || '');

        // Load saved theme
        setSelectedTheme(fetchedCard.theme || 'default');

        // Load links, stats, clicks in parallel
        const [cardLinks] = await Promise.all([
          fetchCardLinks(fetchedCard.id).catch(() => [] as CardLink[]),
        ]);
        const sortedLinks = cardLinks.sort((a, b) => a.order - b.order);
        setLinks(sortedLinks);

        // Extract custom categories not in the default list
        const uniqueCats = Array.from(new Set(sortedLinks.map((l) => l.category || "Other")))
          .filter((c) => !LINK_CATEGORIES.includes(c));
        setCustomCategories(uniqueCats);

        fetchVisitStats(fetchedCard.id).then(setVisitStats).catch(() => {});
        fetchLinkClicks(fetchedCard.id).then(setLinkClicks).catch(() => {});
      } else {
        // Pre-fill form for new card creation. If the visitor picked a handle
        // on the Landing hero before signing up, AuthSync persisted it to
        // unsafeMetadata.desiredHandle — prefer that over the email-derived
        // default so the URL they previewed becomes the URL they actually get.
        const meta = (user?.unsafeMetadata || {}) as Record<string, unknown>;
        const desired = typeof meta.desiredHandle === "string" ? sanitizeHandle(meta.desiredHandle) : "";
        const defaultHandle = fetchedUser.username || email.split("@")[0];
        setCardForm({
          name: `${fetchedUser.firstName || ""} ${fetchedUser.lastName || ""}`.trim(),
          position: "",
          company: fetchedUser.company || "",
          email: fetchedUser.email,
          phoneNumber: fetchedUser.phoneNumber || "",
          officeAddress: fetchedUser.shippingAddress || "",
          bio: fetchedUser.bio || "",
          username: desired && desired.length >= 2 ? desired : defaultHandle,
        });
      }
    } catch (err) {
      addToast("Failed to load your virtual card data. Please refresh.", "error");
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  const isUserReady = !!user;
  useEffect(() => {
    if (isUserReady) {
      loadData();
    }
  }, [isUserReady, loadData]);

  // Reload links and stats when the selected card changes
  useEffect(() => {
    if (!card) return;
    const loadCardData = async () => {
      try {
        const cardLinks = await fetchCardLinks(card.id);
        setLinks(cardLinks.sort((a: CardLink, b: CardLink) => a.order - b.order));

        const stats = await fetchVisitStats(card.id);
        setVisitStats(stats);

        const clicks = await fetchLinkClicks(card.id);
        setLinkClicks(clicks);
      } catch {
        // silently handle errors
      }
    };
    loadCardData();

    // Update form to reflect selected card
    setCardForm(prev => ({
      ...prev,
      name: card.name || "",
      position: card.position || "",
      company: card.company || "",
      email: card.email || "",
      phoneNumber: card.phoneNumber || "",
      username: card.username || "",
    }));
  }, [card?.id]); // eslint-disable-line react-hooks/exhaustive-deps



  /* ---------- Card create / update ---------- */

  const handleSaveCard = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!dbUser) return;

    // Basic validation. BL-008: the public URL is the account handle, edited in
    // Profile — there is no per-card username to set here. The server
    // auto-generates the internal slug (digital_cards.username) on create.
    if (!cardForm.name.trim() || !cardForm.email.trim()) {
      addToast("Name and email are required.", "error");
      return;
    }

    setSavingProfile(true);
    try {
      if (card) {
        // Update existing card. `theme` is part of the same Save now that the
        // theme picker lives inside the three-pane editor (no more standalone
        // Apply Theme button).
        const updated = await updateCard(card.id, {
          name: cardForm.name,
          position: cardForm.position,
          company: cardForm.company || undefined,
          email: cardForm.email,
          phoneNumber: cardForm.phoneNumber || undefined,
          wideLogo: wideLogo || undefined,
          squareLogo: squareLogo || undefined,
          logoStyle: logoStyle,
          showProBadge: showProBadge,
          badgeStyle: badgeStyle,
          profilePhoto: profilePhoto || undefined,
          resumeData: resumeFile || undefined,
          resumeFilename: resumeFilename || undefined,
          resumeType: resumeType || undefined,
          showEmail: showEmail,
          showPhone: showPhone,
          showContactBox: showContactBox,
          showAddress: showAddress,
          vcfIncludeEmail: vcfIncludeEmail,
          vcfIncludePhone: vcfIncludePhone,
          vcfIncludeAddress: vcfIncludeAddress,
          vcfIncludeWebsite: vcfIncludeWebsite,
          theme: selectedTheme,
        } as any);
        setCards(prev => prev.map(c => c.id === updated.id ? updated : c));
        setEditing(false);
        addToast("Virtual card updated successfully!", "success");
      } else {
        // BL-008: no per-card username to claim. The server auto-generates the
        // internal slug; the public URL is the account handle.
        const created = await createCard({
          userId: dbUser.id,
          name: cardForm.name,
          position: cardForm.position,
          company: cardForm.company || undefined,
          email: cardForm.email,
          phoneNumber: cardForm.phoneNumber || undefined,
          wideLogo: wideLogo || undefined,
          squareLogo: squareLogo || undefined,
          logoStyle: logoStyle,
          showProBadge: showProBadge,
          badgeStyle: badgeStyle,
          profilePhoto: profilePhoto || undefined,
          resumeData: resumeFile || undefined,
          resumeFilename: resumeFilename || undefined,
          resumeType: resumeType || undefined,
          showEmail: showEmail,
          showPhone: showPhone,
          showContactBox: showContactBox,
          showAddress: showAddress,
          vcfIncludeEmail: vcfIncludeEmail,
          vcfIncludePhone: vcfIncludePhone,
          vcfIncludeAddress: vcfIncludeAddress,
          vcfIncludeWebsite: vcfIncludeWebsite,
        });
        setCards(prev => [...prev, created]);
        setSelectedCardIndex(cards.length); // select the newly created card
        addToast("Your virtual card has been created!", "success");
      }

      // Also persist bio and office address on the user record
      if (cardForm.bio !== (dbUser.bio || "") || cardForm.officeAddress !== (dbUser.shippingAddress || "")) {
        const updatedUser = await syncUser({
          username: dbUser.username,
          email: dbUser.email,
          firstName: dbUser.firstName,
          lastName: dbUser.lastName,
          bio: cardForm.bio,
          shippingAddress: cardForm.officeAddress,
        });
        setDbUser(updatedUser);
      }
    } catch (err: any) {
      addToast(err.message || "Failed to save virtual card.", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  /* ---------- Active card preset ---------- */

  const handleSetActiveCard = async (cardId: string) => {
    if (!dbUser) return;
    const previous = dbUser.activeCardId ?? null;
    setDbUser({ ...dbUser, activeCardId: cardId });
    try {
      const updated = await updateUser(dbUser.id, { activeCardId: cardId });
      setDbUser(updated);
      addToast("Active card updated.", "success");
    } catch (err) {
      // Surface the real error in dev so we can debug "fails to update"
      // reports instead of silently reverting.
      console.error('[handleSetActiveCard] failed', err);
      setDbUser({ ...dbUser, activeCardId: previous });
      const message = err instanceof Error ? err.message : 'Failed to update active card.';
      addToast(message, "error");
    }
  };

  const startRename = (cardToRename: Card) => {
    setRenamingCardId(cardToRename.id);
    setRenameDraft(cardToRename.adminLabel || "");
  };

  // adminLabel is owner-only — never shown to visitors. The public name on
  // the card stays untouched here. An empty input clears the label.
  const commitRename = async (cardId: string) => {
    const target = cards.find((c) => c.id === cardId);
    setRenamingCardId(null);
    if (!target) return;
    const next = renameDraft.trim();
    const nextLabel = next.length ? next : null;
    if (nextLabel === (target.adminLabel ?? null)) return;
    try {
      const updated = await updateCard(cardId, { adminLabel: nextLabel });
      setCards((prev) => prev.map((c) => (c.id === cardId ? updated : c)));
      addToast("Card renamed.", "success");
    } catch {
      addToast("Failed to rename card.", "error");
    }
  };

  // Industry-picker completion. Persist industry to the user, stash the
  // preset links (custom fields stripped on free), seed the card create form
  // with the persona's title placeholder, and switch into new-card mode.
  // The actual link creation happens in the effect below once the card lands.
  const handlePickerComplete = async (result: IndustryPickerResult) => {
    if (!dbUser) return;

    // Skip — record a sentinel so the picker doesn't reappear on next visit.
    if ("skipped" in result) {
      try {
        const updated = await updateUser(dbUser.id, { industry: "skipped" });
        setDbUser(updated);
      } catch { /* best-effort; user can still proceed */ }
      return;
    }

    // Teams (or any redirect) — record industry, route them away.
    if ("redirectTo" in result) {
      try {
        await updateUser(dbUser.id, { industry: result.industry });
      } catch { /* best-effort */ }
      window.location.href = result.redirectTo;
      return;
    }

    // Persona selection.
    const linksRaw = result.preset.densities![result.density];
    const links = isPaidUser ? linksRaw : stripCustomFieldsForFree(linksRaw);
    setPendingPresetLinks(links);

    try {
      const updated = await updateUser(dbUser.id, { industry: result.industry });
      setDbUser(updated);
    } catch { /* best-effort */ }

    // Seed the create-card form with the persona's title placeholder.
    setCardForm((prev) => ({
      ...prev,
      position: prev.position || result.preset.positionPlaceholder || "",
    }));
    setSelectedCardIndex(-1);
  };

  // After a card lands and pendingPresetLinks is non-empty, serially create
  // each preset link against the new card. Cleared after first run.
  useEffect(() => {
    if (!card || !pendingPresetLinks || pendingPresetLinks.length === 0) return;
    let cancelled = false;
    const linksToCreate = pendingPresetLinks;
    setPendingPresetLinks(null);

    (async () => {
      for (let i = 0; i < linksToCreate.length; i++) {
        if (cancelled) return;
        const l = linksToCreate[i];
        try {
          await createCardLink({
            cardId: card.id,
            title: l.title,
            url: l.urlHint || "",
            description: l.description,
            icon: l.icon,
            category: l.category,
            order: i,
            linkType: l.linkType,
            formConfig: l.formConfig ?? null,
          });
        } catch { /* skip individual failures */ }
      }
      if (cancelled) return;
      try {
        const fresh = await fetchCardLinks(card.id);
        if (!cancelled) setLinks(fresh.sort((a, b) => a.order - b.order));
      } catch { /* */ }
      addToast(
        `Card ready — ${linksToCreate.length} preset ${linksToCreate.length === 1 ? "link" : "links"} added.`,
        "success",
      );
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, pendingPresetLinks]);

  // Stale active-card guard. If the user's stored active_card_id no longer
  // matches any of their cards (e.g. the card was deleted from another tab),
  // clear it on the server so /card/handle falls through to the default
  // resolution and the UI doesn't show a phantom active state.
  useEffect(() => {
    if (!dbUser?.activeCardId || cards.length === 0) return;
    const stillExists = cards.some(c => String(c.id) === String(dbUser.activeCardId));
    if (stillExists) return;
    let cancelled = false;
    (async () => {
      try {
        const updated = await updateUser(dbUser.id, { activeCardId: null });
        if (!cancelled) setDbUser(updated);
      } catch {
        // best-effort; backend DELETE handler also clears this on its own
      }
    })();
    return () => { cancelled = true; };
  }, [dbUser, cards]);

  /* ---------- Link CRUD ---------- */

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!card) return;

    if (links.length >= maxLinks) {
      addToast(`Link limit reached. ${isPaidUser ? '' : 'Upgrade to Pro for more.'}`, 'error');
      return;
    }

    try {
      const isContactForm = newLink.linkType === 'contact_form';
      // For contact-form links, the public-facing "buttonLabel" doubles as the
      // link's title — one input, no risk of internal/public divergence.
      const resolvedTitle = isContactForm
        ? (newLink.formConfig.buttonLabel?.trim() || 'Contact me')
        : newLink.title;
      const created = await createCardLink({
        cardId: card.id,
        title: resolvedTitle,
        url: isContactForm ? '' : newLink.url,
        description: newLink.description || undefined,
        icon: isContactForm ? 'mail' : newLink.icon,
        category: newLink.category,
        order: links.length,
        linkType: newLink.linkType,
        formConfig: isContactForm ? newLink.formConfig : undefined,
      });
      setLinks([...links, created]);
      // Track new custom category if not already known
      if (created.category && !LINK_CATEGORIES.includes(created.category) && !customCategories.includes(created.category)) {
        setCustomCategories([...customCategories, created.category]);
      }
      setNewLink({
        title: "", url: "", description: "", icon: "link", category: "Other",
        linkType: 'url', formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
      });
      setShowAddLinkForm(false);
      addToast("Link added!", "success");
      // Auto-fetch OG metadata in background — URL links only.
      if (!isContactForm) {
        fetchLinkPreview(newLink.url).then(async (preview) => {
          if (preview.title || preview.image || preview.description) {
            try {
              await updateCardLink(created.id, {
                ogTitle: preview.title || undefined,
                ogDescription: preview.description || undefined,
                ogImage: preview.image || undefined,
                ogSiteName: preview.siteName || undefined,
              });
              // Update local state
              setLinks(prev => prev.map(l =>
                l.id === created.id
                  ? { ...l, ogTitle: preview.title, ogDescription: preview.description, ogImage: preview.image, ogSiteName: preview.siteName } as any
                  : l
              ));
            } catch {}
          }
        }).catch(() => {});
      }
    } catch (err) {
      addToast("Failed to add link.", "error");
    }
  };

  const handleUpdateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLink) return;

    const isContactForm = editingLink.linkType === 'contact_form';
    try {
      const resolvedTitle = isContactForm
        ? (editingLink.formConfig?.buttonLabel?.trim() || editingLink.title || 'Contact me')
        : editingLink.title;
      const updated = await updateCardLink(editingLink.id, {
        title: resolvedTitle,
        url: isContactForm ? '' : editingLink.url,
        description: editingLink.description,
        icon: editingLink.icon,
        category: editingLink.category,
        // formConfig only sent for contact-form links so we don't accidentally
        // clear it on URL-link updates that don't carry it.
        ...(isContactForm ? { formConfig: editingLink.formConfig ?? EMPTY_FORM_CONFIG } : {}),
      });
      setLinks(links.map((l) => (l.id === editingLink.id ? updated : l)));
      setEditingLink(null);
      addToast("Link updated!", "success");
    } catch (err) {
      addToast("Failed to update link.", "error");
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await deleteCardLink(linkId);
      setLinks(links.filter((l) => l.id !== linkId));
      addToast("Link deleted.", "success");
    } catch (err) {
      addToast("Failed to delete link.", "error");
    }
  };

  const moveLink = async (linkId: string, direction: "up" | "down") => {
    const idx = links.findIndex((l) => l.id === linkId);
    if (idx === -1) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= links.length) return;

    const reordered = [...links];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    reordered[idx].order = idx;
    reordered[newIdx].order = newIdx;
    setLinks(reordered);

    try {
      await updateCardLink(linkId, { order: newIdx });
      await updateCardLink(reordered[newIdx].id, { order: idx });
    } catch {
      addToast("Failed to reorder links.", "error");
    }
  };

  /* ---------- Sharing helpers ---------- */

  const downloadQRCode = () => {
    if (!card) return;
    const downloadUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(cardUrlQr)}&color=${qrFgColor}&bgcolor=${qrBgColor}`;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${handle || card.username}-qr-code.png`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ---------- Logo upload ---------- */

  // Reads a logo file as a data URL, validating it (image MIME + 2MB cap), then
  // auto-detects the aspect ratio and routes the image to the slot that matches
  // the *detected* style — clearing the other slot. The detected style drives
  // both the slot written to AND logoStyle, so they can never diverge (BL-006):
  // a wide wordmark always lands in wideLogo with logoStyle='wide', a square
  // mark in squareLogo with logoStyle='square'. Any size/aspect is accepted.
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validateLogoFile(file);
    if (error) { addToast(error, 'error'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const slots = routeLogo(dataUrl, styleForDimensions(img.width, img.height));
        setLogoStyle(slots.logoStyle);
        setWideLogo(slots.wideLogo);
        setSquareLogo(slots.squareLogo);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  // Rotates the currently-loaded logo 90° clockwise, baking the rotation into
  // the pixels (BL-007). Some logos are authored/exported with rotated source
  // pixels (e.g. a 72×288 portrait wordmark) — the app was faithfully rendering
  // them sideways. After rotating, the NEW (swapped) dimensions feed
  // styleForDimensions + routeLogo so the rotated logo lands in the correct
  // slot: a 72×288 portrait → 288×72 → wide → wideLogo, logoStyle='wide'.
  const handleRotateLogo = async () => {
    const current = logoStyle === 'wide' ? wideLogo : squareLogo;
    if (!current) return;
    try {
      const { dataUrl, width, height } = await rotateImage90(current);
      if (!isUnderLogoCap(dataUrl)) { addToast('Image must be under 2MB', 'error'); return; }
      const slots = routeLogo(dataUrl, styleForDimensions(width, height));
      setLogoStyle(slots.logoStyle);
      setWideLogo(slots.wideLogo);
      setSquareLogo(slots.squareLogo);
    } catch {
      addToast('Could not rotate that image. Try a different file.', 'error');
    }
  };

  /* ---------- Render ---------- */

  if (!user || loading) {
    return <LoadingSkeleton />;
  }

  // Post-signup industry picker. Renders only when the user is fresh — no
  // cards yet AND no industry recorded (null = first-time visitor). Skipping
  // writes industry='skipped' so the picker doesn't reappear; selecting writes
  // industry=<persona> and stages preset links via handlePickerComplete.
  if (dbUser && cards.length === 0 && !dbUser.industry) {
    return (
      <IndustryPicker
        isPaidUser={isPaidUser}
        displayName={dbUser.firstName || undefined}
        onComplete={handlePickerComplete}
      />
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-coffee-900 mb-2">Sign In Required</h1>
          <p className="text-coffee-600">Sign in to create your virtual business card.</p>
        </div>
      </div>
    );
  }

  const isNewCard = !card;

  // Card object combining saved card with live form/state, used by CardRenderer for preview
  const previewCard: CardRendererCard | null = card ? {
    id: card.id,
    userId: card.userId,
    username: card.username,
    name: cardForm.name || card.name,
    position: cardForm.position || card.position,
    email: cardForm.email || card.email,
    phoneNumber: cardForm.phoneNumber || card.phoneNumber,
    websiteUrl: card.websiteUrl,
    company: cardForm.company || card.company,
    wideLogo: wideLogo || undefined,
    squareLogo: squareLogo || undefined,
    logoStyle,
    showProBadge,
    badgeStyle,
    theme: selectedTheme,
    profilePhoto: profilePhoto || undefined,
    hasResume: (card as any).hasResume,
    showEmail,
    showPhone,
    showContactBox,
    showAddress,
    vcfIncludeEmail,
    vcfIncludePhone,
    vcfIncludeAddress,
    vcfIncludeWebsite,
  } : null;

  return (
    <div>
      {/* Page header */}
      <div className="border-b border-coffee-100 bg-white -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow mb-1.5">Your card</div>
            <h1 className="text-display text-3xl sm:text-4xl text-coffee-900">
              {cardForm.name || "Your card"}
            </h1>
            {/* BL-008: the public URL is the account handle (set in Profile).
                Show it here, or prompt the user to set a handle if missing so
                they never see a broken link. */}
            {card && (
              hasHandle ? (
                <p className="mt-1.5 text-sm text-coffee-500 font-mono break-all">
                  {window.location.origin}/card/{handle}
                </p>
              ) : (
                <p className="mt-1.5 text-sm text-coffee-600">
                  Set a handle in your{" "}
                  <Link to="/profile" className="underline font-medium text-coffee-800 hover:text-coffee-900">
                    Profile
                  </Link>{" "}
                  to publish your card link.
                </p>
              )
            )}
          </div>
          {card && (
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              {hasHandle && (
                <Button
                  variant="secondary"
                  size="sm"
                  href={cardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View card
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                to="/order-card"
              >
                Order physical
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">

        {/* Pro thank you banner — dismissable */}
        {isPaidUser && !proBannerDismissed && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
            <p className="text-amber-900 text-sm">Thank you for being a Pro member. Your support means a lot. — Sean</p>
            <button
              onClick={() => { sessionStorage.setItem('pro_banner_dismissed', 'true'); setProBannerDismissed(true); }}
              className="text-amber-600 hover:text-amber-800 ml-4 flex-shrink-0 p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {/* Tab state controlled via URL param (?tab=stats) and top nav */}

        {/* My Card header — V-B "tabs + active rail". Replaces the old
            "Active preset · Pro" panel and the pill-style card selector.
            One-card state: dark hero tile + "Add a second card" tile.
            Multi-card state: live-at rail + tab grid where the editing tile
            goes dark and the active card carries a green badge. */}
        {cards.length > 0 && dbUser && (
          <div className="max-w-3xl mx-auto mb-6">
            {cards.length === 1 ? (
              /* ─── One-card state ─── */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Hero tile */}
                <div className="bg-coffee-900 text-cream-50 rounded-2xl p-5 sm:col-span-2">
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <p className="text-[10px] font-mono uppercase tracking-[0.18em] opacity-70">
                      Your card
                    </p>
                    {renamingCardId !== cards[0].id && (
                      <button
                        type="button"
                        onClick={() => startRename(cards[0])}
                        className="text-[10px] font-mono uppercase tracking-[0.18em] opacity-70 hover:opacity-100 inline-flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" />
                        Rename
                      </button>
                    )}
                  </div>
                  {renamingCardId === cards[0].id ? (
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={() => commitRename(cards[0].id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(cards[0].id); }
                        if (e.key === 'Escape') setRenamingCardId(null);
                      }}
                      maxLength={80}
                      placeholder="Label this card (e.g. YC Demo Day)"
                      className="w-full bg-transparent border-b border-cream-50/40 focus:border-cream-50 focus:outline-none font-display text-2xl"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => startRename(cards[0])}
                      className="text-left font-display text-2xl border-b border-dashed border-cream-50/30 hover:border-cream-50/60 pb-0.5"
                      title="Owner-only label — visitors never see this"
                    >
                      {cards[0].adminLabel || 'Untitled card'}
                    </button>
                  )}
                {/* URL editing moved to /profile — keeps the card surface clean. */}
              </div>

                {/* Add second card tile */}
                {isPaidUser ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCardIndex(-1);
                      setCardForm({
                        name: '',
                        position: '',
                        company: dbUser?.company || '',
                        email: dbUser?.email || '',
                        phoneNumber: dbUser?.phoneNumber || '',
                        officeAddress: dbUser?.shippingAddress || '',
                        bio: dbUser?.bio || '',
                        username: '',
                      });
                    }}
                    className="bg-cream-50 border border-dashed border-latte-300 rounded-2xl p-5 hover:border-coffee-700 hover:bg-cream-100 text-left transition-colors"
                  >
                    <span className="w-9 h-9 rounded-full border border-latte-300 grid place-items-center mb-3">
                      <Plus className="w-4 h-4 text-coffee-700" />
                    </span>
                    <p className="font-medium text-coffee-900 text-sm">Add a second card</p>
                    <p className="text-xs text-coffee-600 mt-1">Up to {MAX_CARDS_PAID} on Pro. One per role works well.</p>
                  </button>
                ) : (
                  <Link
                    to="/pricing"
                    className="bg-cream-50 border border-dashed border-latte-300 rounded-2xl p-5 hover:border-coffee-700 hover:bg-cream-100 block transition-colors"
                  >
                    <span className="inline-block px-2 py-0.5 bg-coffee-900 text-cream-50 text-[10px] font-mono uppercase tracking-[0.18em] rounded mb-3">Pro</span>
                    <p className="font-medium text-coffee-900 text-sm">Add a second card</p>
                    <p className="text-xs text-coffee-600 mt-1">Pro keeps up to {MAX_CARDS_PAID} cards under one handle.</p>
                  </Link>
                )}
              </div>
            ) : (
              /* ─── Multi-card state ─── */
              <>
                {/* Active rail — names which card the public handle currently
                    serves so admins can see at a glance. URL editing moved to
                    /profile to keep the card surface clean. */}
                {(() => {
                  const activeCard = cards.find(c => String(c.id) === String(dbUser.activeCardId ?? '')) ?? cards[0];
                  return (
                    <div className="bg-cream-100 border border-latte-300 rounded px-4 py-2 mb-3 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-coffee-700">
                        <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-coffee-500">
                          Public card
                        </span>
                        <span className="mx-1.5 text-coffee-400">→</span>
                        <span className="font-medium text-coffee-900">{activeCard.name || 'Untitled'}</span>
                      </p>
                    </div>
                  );
                })()}

                {/* Card tab grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {cards.map((c, i) => {
                    const isActive = String(dbUser.activeCardId ?? '') === String(c.id);
                    const isEditing = i === selectedCardIndex;
                    const isRenaming = renamingCardId === c.id;
                    return (
                      <div
                        key={c.id}
                        onClick={() => { if (!isRenaming) setSelectedCardIndex(i); }}
                        className={`rounded-2xl p-4 cursor-pointer transition-all ${
                          isEditing
                            ? 'bg-coffee-900 text-cream-50 ring-2 ring-coffee-900 ring-offset-2 ring-offset-cream-50'
                            : 'bg-cream-50 border border-latte-300 hover:bg-cream-100'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <p className={`text-[10px] font-mono uppercase tracking-[0.18em] truncate ${isEditing ? 'opacity-70' : 'text-coffee-500'}`}>
                            Card {i + 1}{isEditing ? ' · editing' : ''}
                          </p>
                          {isActive && (
                            <span
                              className={`text-[10px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded flex-shrink-0 ${
                                isEditing ? 'bg-cream-50/15 text-cream-50' : 'bg-success-50 text-success-700'
                              }`}
                            >
                              Active
                            </span>
                          )}
                        </div>
                        {isRenaming ? (
                          <input
                            autoFocus
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => commitRename(c.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); commitRename(c.id); }
                              if (e.key === 'Escape') setRenamingCardId(null);
                            }}
                            maxLength={80}
                            placeholder="Label this card (e.g. YC Demo Day)"
                            className={`w-full bg-transparent border-b focus:outline-none text-sm font-medium ${
                              isEditing ? 'border-cream-50/40 focus:border-cream-50' : 'border-coffee-300 focus:border-coffee-700 text-coffee-900'
                            }`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); startRename(c); }}
                            className={`text-left text-sm font-medium border-b border-dashed pb-0.5 ${
                              isEditing
                                ? 'border-cream-50/30 hover:border-cream-50/60'
                                : 'text-coffee-900 border-latte-300 hover:border-coffee-700'
                            }`}
                            title="Owner-only label — visitors never see this"
                          >
                            {c.adminLabel || 'Untitled card'}
                          </button>
                        )}
                        {!isActive && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleSetActiveCard(c.id); }}
                            className={`mt-4 inline-block text-[11px] underline underline-offset-2 ${
                              isEditing ? 'text-cream-50/80 hover:text-cream-50 decoration-cream-50/40' : 'text-coffee-700 hover:text-coffee-900 decoration-latte-300'
                            }`}
                          >
                            Make active
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Add new tile — disabled when at limit */}
                  {cards.length < maxCards ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCardIndex(-1);
                        setCardForm({
                          name: '',
                          position: '',
                          company: dbUser?.company || '',
                          email: dbUser?.email || '',
                          phoneNumber: dbUser?.phoneNumber || '',
                          officeAddress: dbUser?.shippingAddress || '',
                          bio: dbUser?.bio || '',
                          username: '',
                        });
                      }}
                      className={`bg-cream-50 border border-dashed border-latte-300 rounded-2xl p-4 hover:border-coffee-700 hover:bg-cream-100 text-left transition-colors flex flex-col ${
                        selectedCardIndex === -1 ? 'ring-2 ring-coffee-900 ring-offset-2 ring-offset-cream-50' : ''
                      }`}
                    >
                      <span className="w-8 h-8 rounded-full border border-latte-300 grid place-items-center mb-2">
                        <Plus className="w-4 h-4 text-coffee-700" />
                      </span>
                      <p className="text-sm font-medium text-coffee-900">New card</p>
                      <p className="text-xs text-coffee-600 mt-0.5">{cards.length} of {maxCards}</p>
                    </button>
                  ) : (
                    <div className="bg-cream-50 border border-dashed border-latte-300 rounded-2xl p-4 text-coffee-400 flex flex-col items-start opacity-60 cursor-not-allowed">
                      <span className="w-8 h-8 rounded-full border border-latte-300 grid place-items-center mb-2">
                        <Plus className="w-4 h-4" />
                      </span>
                      <p className="text-sm font-medium">{maxCards} of {maxCards} used</p>
                      <p className="text-xs mt-0.5">Delete a card to free a slot.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {(<>

        {/* ============================================================ */}
        {/*  CARD VIEW / EDIT (when card exists)                         */}
        {/* ============================================================ */}
        {previewCard && !editing && (
          <div className="max-w-md mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/15">
              <CardRenderer
                card={previewCard}
                links={links as CardRendererLink[]}
                bio={cardForm.bio}
                address={cardForm.officeAddress}
                isPro={isPaidUser}
                isSignedIn
                preview
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Button
                variant="primary"
                className="flex-1 h-14 sm:h-11"
                onClick={() => setEditing(true)}
              >
                Edit my Card
              </Button>
              <Button
                variant="secondary"
                className="flex-1 h-14 sm:h-11"
                onClick={() => {
                  setShowLinksEditor(true);
                  // Scroll once the panel has had a frame to render.
                  setTimeout(() => {
                    document.getElementById('links-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }}
              >
                Edit my Links
              </Button>
            </div>
          </div>
        )}

        {/* Edit my Card — three-pane layout on desktop, tabbed on mobile.
            Theme rail (left) · form sections (middle) · live preview (right).
            See Product Proposals/13-edit-my-card-layout.html for the design. */}
        {card && editing && previewCard && (
          <div className="mt-6 mb-8">
            {/* Header row: title + Save/Cancel always reachable. */}
            <div className="flex items-center justify-between mb-5 px-1">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300">Editing</p>
                <h2 className="text-2xl font-bold text-coffee-900 dark:text-cream-50">Your card</h2>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" size="sm" loading={savingProfile} onClick={() => handleSaveCard()}>
                  {savingProfile ? "Saving" : "Save"}
                </Button>
              </div>
            </div>

            {/* Mobile: live preview sticky-at-top + tab strip. Desktop hides
                this whole block; the preview moves to the right rail and tabs
                disappear (all three panes are visible at once). */}
            <div className="lg:hidden">
              <div className="mb-4">
                <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-2 text-center">Live preview</p>
                <div className="max-w-xs mx-auto rounded-2xl overflow-hidden shadow-coffee-lg ring-1 ring-black/10">
                  <CardRenderer
                    card={previewCard}
                    links={links as CardRendererLink[]}
                    bio={cardForm.bio}
                    address={cardForm.officeAddress}
                    isPro={isPaidUser}
                    isSignedIn
                    preview
                  />
                </div>
              </div>
              <div className="flex gap-1 border-b border-coffee-200 dark:border-coffee-700 mb-4">
                {(['content', 'theme', 'display'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setEditTab(tab)}
                    className={`px-4 py-2.5 text-xs font-semibold capitalize transition-colors ${
                      editTab === tab
                        ? 'text-coffee-900 dark:text-cream-50 border-b-2 border-coffee-900 dark:border-cream-50 -mb-px'
                        : 'text-coffee-500 dark:text-cream-300 hover:text-coffee-700 dark:hover:text-cream-100'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:grid lg:grid-cols-[220px_1fr_300px] lg:gap-6">
              {/* ─── Theme rail (left on desktop, Theme tab on mobile) ─── */}
              <aside className={`${editTab === 'theme' ? 'block' : 'hidden'} lg:block mb-4 lg:mb-0`}>
                <div className="lg:sticky lg:top-20 bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-3">Theme</p>
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                    {THEME_OPTIONS.map((t) => {
                      const isLocked = t.proOnly && !isPaidUser;
                      const isSelected = selectedTheme === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            if (isLocked) {
                              addToast('This theme is available on Pro. Upgrade to unlock.', 'info');
                              return;
                            }
                            setSelectedTheme(t.id);
                          }}
                          className={`relative p-1.5 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? 'border-coffee-900 dark:border-cream-50 ring-2 ring-coffee-300 dark:ring-coffee-700'
                              : 'border-coffee-200 dark:border-coffee-700 hover:border-coffee-400 dark:hover:border-coffee-500'
                          }`}
                        >
                          <div className="rounded h-10 overflow-hidden flex mb-1">
                            <div className={`${t.light} w-1/2 flex items-center justify-center`}>
                              <span className={`text-[11px] font-bold ${t.lightText}`}>Aa</span>
                            </div>
                            <div className={`${t.dark} w-1/2 flex items-center justify-center`}>
                              <span className={`text-[11px] font-bold ${t.darkText}`}>Aa</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-1 px-0.5">
                            <span className="text-[11px] text-coffee-700 dark:text-cream-200 truncate">{t.name}</span>
                            {isLocked && (
                              <span className="font-mono text-[9px] uppercase tracking-wider text-accent-700 flex-shrink-0">Pro</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-coffee-500 dark:text-cream-300 mt-3 leading-snug">
                    Visitors see the light or dark side based on their device's mode.
                  </p>
                </div>
              </aside>

              {/* ─── Form middle pane (Content + Display tabs on mobile, always visible on desktop) ─── */}
              <main className={`${editTab === 'theme' ? 'hidden' : 'block'} lg:block`}>
                {/* Content sections — Identity, Contact, Logo */}
                <div className={`${editTab === 'display' ? 'hidden lg:block' : 'block'} space-y-4`}>
                  {/* Identity */}
                  <section className="bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-4">Identity</p>
                    <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                      {/* Avatar + change photo */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-2">
                        {logoStyle === 'wide' && wideLogo ? (
                          <div className="w-20 h-20 flex items-center justify-center">
                            <img src={wideLogo} alt="Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : squareLogo && logoStyle === 'square' ? (
                          <div className="w-20 h-20 rounded-full ring-4 ring-white dark:ring-coffee-700 shadow-lg overflow-hidden">
                            <img src={squareLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                          </div>
                        ) : profilePhoto ? (
                          <div className="w-20 h-20 rounded-full ring-4 ring-white dark:ring-coffee-700 shadow-lg overflow-hidden">
                            <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-coffee-600 dark:bg-coffee-500 rounded-full flex items-center justify-center ring-4 ring-white dark:ring-coffee-700 shadow-lg">
                            <span className="text-white text-xl font-bold">
                              {cardForm.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                        )}
                        <div className="flex gap-2 justify-center">
                          <label className="cursor-pointer text-[11px] text-coffee-600 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 underline">
                            Change photo
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 2 * 1024 * 1024) { addToast('Photo must be under 2MB', 'error'); return; }
                                const reader = new FileReader();
                                reader.onload = (ev) => setProfilePhoto(ev.target?.result as string);
                                reader.readAsDataURL(file);
                              }}
                              className="hidden"
                            />
                          </label>
                          {profilePhoto && (
                            <button type="button" onClick={() => setProfilePhoto(null)} className="text-[11px] text-danger-500 hover:text-danger-700 underline">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Name / Position / Company */}
                      <div className="flex-1 space-y-3 min-w-0">
                        <div>
                          <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-1">Name</label>
                          <input
                            value={cardForm.name}
                            onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                            className="w-full h-10 px-3 border border-coffee-200 dark:border-coffee-700 rounded bg-white dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 focus:border-coffee-600 focus:outline-none"
                            placeholder="Your Name"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-1">Position</label>
                            <input
                              value={cardForm.position}
                              onChange={(e) => setCardForm({ ...cardForm, position: e.target.value })}
                              className="w-full h-10 px-3 border border-coffee-200 dark:border-coffee-700 rounded bg-white dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 focus:border-coffee-600 focus:outline-none"
                              placeholder="Founder / Realtor / …"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-1">Company</label>
                            <input
                              value={cardForm.company}
                              onChange={(e) => setCardForm({ ...cardForm, company: e.target.value })}
                              className="w-full h-10 px-3 border border-coffee-200 dark:border-coffee-700 rounded bg-white dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 focus:border-coffee-600 focus:outline-none"
                              placeholder="Optional"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="mt-4">
                      <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-1">Bio</label>
                      <textarea
                        value={cardForm.bio}
                        onChange={(e) => setCardForm({ ...cardForm, bio: e.target.value })}
                        className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded bg-white dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 focus:border-coffee-600 focus:outline-none resize-none"
                        placeholder="One sentence about you or your work."
                        rows={2}
                        maxLength={200}
                      />
                      <p className="text-[10px] text-coffee-500 dark:text-cream-300 mt-1 text-right">{cardForm.bio.length}/200</p>
                    </div>
                  </section>

                  {/* Contact info */}
                  <section className="bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-4">Contact info</p>
                    <div className="space-y-3">
                      {[
                        { label: 'Email', type: 'email', value: cardForm.email, set: (v: string) => setCardForm({ ...cardForm, email: v }), placeholder: 'you@company.com', visible: showEmail, setVisible: setShowEmail },
                        { label: 'Phone', type: 'tel', value: cardForm.phoneNumber, set: (v: string) => setCardForm({ ...cardForm, phoneNumber: v }), placeholder: '(555) 123-4567', visible: showPhone, setVisible: setShowPhone },
                        { label: 'Office address', type: 'text', value: cardForm.officeAddress, set: (v: string) => setCardForm({ ...cardForm, officeAddress: v }), placeholder: 'Optional', visible: showAddress, setVisible: setShowAddress },
                      ].map((f) => (
                        <div key={f.label}>
                          <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-1">{f.label}</label>
                          <div className="flex items-center gap-2">
                            <input
                              type={f.type}
                              value={f.value}
                              onChange={(e) => f.set(e.target.value)}
                              className="flex-1 h-10 px-3 border border-coffee-200 dark:border-coffee-700 rounded bg-white dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 focus:border-coffee-600 focus:outline-none"
                              placeholder={f.placeholder}
                            />
                            <button
                              type="button"
                              onClick={() => f.setVisible(!f.visible)}
                              className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-2 rounded transition-colors ${
                                f.visible
                                  ? 'bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900'
                                  : 'bg-coffee-100 dark:bg-coffee-700 text-coffee-500 dark:text-cream-300'
                              }`}
                              title={f.visible ? 'Currently visible — click to hide' : 'Currently hidden — click to show'}
                            >
                              {f.visible ? 'Show' : 'Hide'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-coffee-500 dark:text-cream-300 mt-3 leading-snug">
                      Show / hide controls whether each field appears on your public card. The Save Contact (vCard) settings live in the Display tab.
                    </p>
                  </section>

                  {/* Logo */}
                  <section className="bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-4">Logo</p>
                    {showLogoUpload || wideLogo || squareLogo ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[11px] font-semibold text-coffee-700 dark:text-cream-200 mb-2">Style</label>
                          <div className="flex rounded-lg overflow-hidden border border-coffee-200 dark:border-coffee-700 w-fit">
                            <button
                              type="button"
                              onClick={() => setLogoStyle('square')}
                              className={`py-2 px-4 text-xs font-semibold transition-colors ${logoStyle === 'square' ? 'bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900' : 'bg-white dark:bg-coffee-900 text-coffee-700 dark:text-cream-200 hover:bg-coffee-50 dark:hover:bg-coffee-800'}`}
                            >
                              Square
                            </button>
                            <button
                              type="button"
                              onClick={() => setLogoStyle('wide')}
                              className={`py-2 px-4 text-xs font-semibold transition-colors ${logoStyle === 'wide' ? 'bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900' : 'bg-white dark:bg-coffee-900 text-coffee-700 dark:text-cream-200 hover:bg-coffee-50 dark:hover:bg-coffee-800'}`}
                            >
                              Wide
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="cursor-pointer bg-coffee-100 dark:bg-coffee-700 hover:bg-coffee-200 dark:hover:bg-coffee-600 text-coffee-700 dark:text-cream-200 font-medium py-2 px-4 rounded text-xs">
                            {(logoStyle === 'wide' ? wideLogo : squareLogo) ? 'Change file' : 'Upload file'}
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                          </label>
                          {logoStyle === 'wide' && wideLogo && (
                            <>
                              <img src={wideLogo} alt="Wide logo preview" className="h-10 object-contain" />
                              <button type="button" onClick={() => setWideLogo(null)} className="text-danger-500 text-xs underline">Remove</button>
                            </>
                          )}
                          {logoStyle === 'square' && squareLogo && (
                            <>
                              <img src={squareLogo} alt="Square logo preview" className="h-10 w-10 object-contain rounded" />
                              <button type="button" onClick={() => setSquareLogo(null)} className="text-danger-500 text-xs underline">Remove</button>
                            </>
                          )}
                        </div>
                        {(wideLogo || squareLogo) && (
                          <button
                            type="button"
                            onClick={handleRotateLogo}
                            className="inline-flex items-center gap-1.5 bg-coffee-100 dark:bg-coffee-700 hover:bg-coffee-200 dark:hover:bg-coffee-600 text-coffee-700 dark:text-cream-200 font-medium py-2 px-3 rounded text-xs"
                          >
                            <RotateCw className="w-3.5 h-3.5" />
                            Rotate 90°
                          </button>
                        )}
                        <p className="text-[10px] text-coffee-500 dark:text-cream-300">
                          Wide or square — any size. PNG or JPG, under 2MB. Transparent PNG recommended.
                          Sideways logo? Use Rotate 90°.
                        </p>
                      </div>
                    ) : (
                      <button onClick={() => setShowLogoUpload(true)} className="w-full border border-dashed border-coffee-300 dark:border-coffee-600 rounded-lg py-3 text-xs text-coffee-500 dark:text-cream-300 hover:text-coffee-700 dark:hover:text-cream-100 hover:border-coffee-400 dark:hover:border-coffee-500 transition-colors">
                        Add a company logo
                      </button>
                    )}
                  </section>
                </div>

                {/* Display & visibility sections — vCard inclusion, Contact box toggle, Pro Badge */}
                <div className={`${editTab === 'content' ? 'hidden lg:block' : 'block'} space-y-4 ${editTab !== 'content' ? '' : 'lg:mt-4'} mt-0 lg:mt-4`}>
                  <section className="bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-4">Display &amp; visibility</p>

                    {/* Contact box toggle */}
                    <div className="flex items-center justify-between pb-4 border-b border-coffee-100 dark:border-coffee-700">
                      <div className="pr-4">
                        <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50">Show Contact Info box</p>
                        <p className="text-[11px] text-coffee-500 dark:text-cream-300 mt-0.5">Toggle the whole Contact Information section on the public card.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowContactBox(!showContactBox)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${showContactBox ? 'bg-coffee-900 dark:bg-cream-50' : 'bg-coffee-300 dark:bg-coffee-700'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-coffee-900 transition-transform ${showContactBox ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* vCard inclusion */}
                    <div className="pt-4">
                      <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50 mb-1">Include in Save Contact (vCard)</p>
                      <p className="text-[11px] text-coffee-500 dark:text-cream-300 mb-3">What gets saved to a phone when someone taps Save Contact.</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { label: 'Email', value: vcfIncludeEmail, setter: setVcfIncludeEmail },
                          { label: 'Phone', value: vcfIncludePhone, setter: setVcfIncludePhone },
                          { label: 'Address', value: vcfIncludeAddress, setter: setVcfIncludeAddress },
                          { label: 'Website', value: vcfIncludeWebsite, setter: setVcfIncludeWebsite },
                        ].map(item => (
                          <button
                            key={item.label}
                            type="button"
                            onClick={() => item.setter(!item.value)}
                            className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                              item.value
                                ? 'bg-coffee-900 dark:bg-cream-50 text-cream-50 dark:text-coffee-900 border-coffee-900 dark:border-cream-50'
                                : 'bg-white dark:bg-coffee-900 text-coffee-500 dark:text-cream-300 border-coffee-200 dark:border-coffee-700 hover:border-coffee-400 dark:hover:border-coffee-500'
                            }`}
                          >
                            {item.value ? '✓ ' : ''}{item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </section>

                  {isPaidUser && (
                    <section className="bg-white dark:bg-coffee-800 rounded-2xl shadow-coffee-lg border border-coffee-100 dark:border-coffee-800 p-5">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-4">Pro Badge</p>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-coffee-700 dark:text-cream-200">Show badge on card</p>
                        <button
                          type="button"
                          onClick={() => setShowProBadge(!showProBadge)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showProBadge ? 'bg-coffee-900 dark:bg-cream-50' : 'bg-coffee-300 dark:bg-coffee-700'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-coffee-900 transition-transform ${showProBadge ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {showProBadge && (
                        <div>
                          <p className="text-[11px] text-coffee-500 dark:text-cream-300 mb-2">Style</p>
                          <div className="flex gap-2">
                            {[
                              { id: 'gold' as const, label: 'Gold' },
                              { id: 'platinum' as const, label: 'Platinum' },
                              { id: 'dark' as const, label: 'Dark' },
                            ].map(style => (
                              <button
                                key={style.id}
                                type="button"
                                onClick={() => setBadgeStyle(style.id)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${BADGE_STYLES[style.id]} ${
                                  badgeStyle === style.id ? 'ring-2 ring-offset-1 ring-coffee-400' : 'opacity-70 hover:opacity-100'
                                }`}
                              >
                                PRO
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </main>

              {/* ─── Live preview right pane (desktop only — mobile shows it above the tabs) ─── */}
              <aside className="hidden lg:block">
                <div className="lg:sticky lg:top-20">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 dark:text-cream-300 mb-2 text-center">Live preview</p>
                  <div className="rounded-2xl overflow-hidden shadow-coffee-lg ring-1 ring-black/10">
                    <CardRenderer
                      card={previewCard}
                      links={links as CardRendererLink[]}
                      bio={cardForm.bio}
                      address={cardForm.officeAddress}
                      isPro={isPaidUser}
                      isSignedIn
                      preview
                    />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* Theme section moved into the grid below */}

        {/* ============================================================ */}
        {/*  NEW CARD CREATION (only when no card exists)                 */}
        {/* ============================================================ */}
        {isNewCard && (
          <div className="bg-white rounded-2xl shadow-coffee-lg p-8">
            {/* Progress indicator for first-time setup */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-coffee-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <span className="text-sm font-medium text-coffee-900">Your Info</span>
              </div>
              <div className="w-12 h-px bg-coffee-300"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-coffee-200 text-coffee-500 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <span className="text-sm font-medium text-coffee-400">Add Links</span>
              </div>
            </div>

            <div className="eyebrow mb-2">New card</div>
            <h2 className="text-display text-3xl text-coffee-900 mb-2">
              Set up your card.
            </h2>
            <p className="text-coffee-600 mb-6">
              Fill in your details and pick a handle. You can update everything later.
            </p>

            <form onSubmit={handleSaveCard} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Name *</label>
                  <input
                    type="text"
                    value={cardForm.name}
                    onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors"
                    placeholder="Jane Smith"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Title / Position</label>
                  <input
                    type="text"
                    value={cardForm.position}
                    onChange={(e) => setCardForm({ ...cardForm, position: e.target.value })}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors"
                    placeholder="Product Designer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Company</label>
                  <input
                    type="text"
                    value={cardForm.company}
                    onChange={(e) => setCardForm({ ...cardForm, company: e.target.value })}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors"
                    placeholder="Acme Inc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={cardForm.email}
                    onChange={(e) => setCardForm({ ...cardForm, email: e.target.value })}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors"
                    placeholder="jane@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={cardForm.phoneNumber}
                    onChange={(e) => setCardForm({ ...cardForm, phoneNumber: e.target.value })}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors"
                    placeholder="(555) 123-4567"
                  />
                </div>
                {/* BL-008: the public URL is the account handle, set in
                    Profile — there is no per-card username field. Show the URL
                    that this card will be reachable at, or prompt to set a
                    handle if the user doesn't have one yet. */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-coffee-700 mb-2">
                    Your public link
                  </label>
                  {hasHandle ? (
                    <p className="text-sm text-coffee-600 font-mono break-all">
                      {window.location.origin}/card/{handle}
                    </p>
                  ) : (
                    <p className="text-sm text-coffee-600">
                      Set a handle in your{" "}
                      <a href="/profile" className="underline font-medium text-coffee-800 hover:text-coffee-900">
                        Profile
                      </a>{" "}
                      to get your public card link.
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-coffee-700 mb-2">Bio</label>
                  <textarea
                    value={cardForm.bio}
                    onChange={(e) => setCardForm({ ...cardForm, bio: e.target.value })}
                    rows={3}
                    maxLength={200}
                    className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 transition-colors resize-none"
                    placeholder="Tell people about yourself in a sentence or two..."
                  />
                  <p className="text-xs text-coffee-500 mt-1">{cardForm.bio.length}/200 characters</p>
                </div>
              </div>

              <div className="pt-4 border-t border-coffee-100">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={savingProfile}
                >
                  {savingProfile ? "Saving" : "Create your card"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* First-time prompt: card created, no links yet */}
        {card && links.length === 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-eyebrow font-semibold text-emerald-700 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Card live
            </div>
            <p className="text-coffee-900 font-semibold mb-1">Your card is set up.</p>
            <p className="text-sm text-coffee-600 mb-3">Add links to make it useful. LinkedIn, a booking calendar, or your portfolio are good starting points.</p>
            {hasHandle && (
              <Button
                variant="ghost"
                size="sm"
                href={cardUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Preview your card
              </Button>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/*  Below-card content: 3-column grid on wide screens           */}
        {/*  Visible when the user clicks Edit my Card or Edit my Links. */}
        {/*  Within the main column, Links is gated by showLinksEditor;  */}
        {/*  Theme is gated by editing. So clicking Edit my Links shows  */}
        {/*  only Links; clicking Edit my Card shows only Theme + the    */}
        {/*  card-edit form already rendered above this grid.            */}
        {/* ============================================================ */}
        {/* Links Management + sidebar grid. Editing mode owns the full width
            via the three-pane layout above, so this grid only renders when the
            links editor is open. */}
        {card && showLinksEditor && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 mt-8">
          {/* Main column - Links + Theme */}
          <div className="order-1 space-y-6">
        {/* ============================================================ */}
        {/*  Links Management — gated on showLinksEditor                 */}
        {/* ============================================================ */}
          {showLinksEditor && (
          <div id="links-section" className="bg-white rounded-2xl shadow-coffee-lg p-6 scroll-mt-20">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <div>
                <div className="eyebrow mb-1">Links</div>
                <h3 className="font-semibold text-coffee-900">
                  <span className="font-mono">{links.length}/{maxLinks}</span> used
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {links.length < maxLinks && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setShowAddLinkForm(true);
                      setNewLink({
                        title: "", url: "", description: "", icon: "link", category: "Other",
                        linkType: 'url', formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
                      });
                    }}
                  >
                    Add link
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowLinksEditor(false);
                    setShowAddLinkForm(false);
                    setEditingLink(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>

            {/* Link limit message */}
            {links.length >= maxLinks && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-center">
                <p className="text-amber-800 text-sm">
                  You've reached your link limit ({maxLinks}).{' '}
                  {!isPaidUser && (
                    <>
                      <a href="/pricing" className="underline font-medium hover:text-amber-900">Upgrade to Pro</a> for up to {MAX_LINKS_PAID} links.
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Link picker — command-palette layout. Hidden once the user
                hits their link cap; the cap banner above carries the message.
                Search dominates; ⌘K focuses. Empty state shows five popular
                tiles + a disclosure that opens the grouped catalog. Active
                search renders a list view with the top match outlined and an
                "↵ Add" hint so Enter feels obvious. */}
            {links.length < maxLinks && (
              <div className="mb-6">
                <div className="relative mb-4">
                  <Search className="w-4 h-4 text-coffee-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    ref={linkSearchRef}
                    type="text"
                    value={linkSearch}
                    onChange={(e) => setLinkSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && linkSearch && filteredPresets[0]) {
                        e.preventDefault();
                        addPresetLink(filteredPresets[0]);
                      } else if (e.key === 'Escape' && linkSearch) {
                        e.preventDefault();
                        setLinkSearch('');
                      }
                    }}
                    placeholder="Search or paste a URL..."
                    className="w-full pl-10 pr-24 py-3 text-base border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500 placeholder:text-coffee-400"
                  />
                  <span className="hidden sm:inline-block absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-coffee-500 bg-coffee-50 border border-coffee-200 rounded">
                    {linkSearch ? '↵ to add' : '⌘ K'}
                  </span>
                </div>

                {linkSearch === '' ? (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 mb-2">Popular</p>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                      {popularPresets.map((preset) => (
                        <button
                          key={preset.title}
                          type="button"
                          onClick={() => addPresetLink(preset)}
                          className={`rounded-lg p-2.5 text-sm transition-colors flex flex-col items-center gap-1.5 border ${
                            preset.title === 'Custom Form'
                              ? 'bg-accent-50 border-accent-500/40 hover:bg-accent-100 hover:border-accent-500'
                              : 'bg-coffee-50 border-coffee-200 hover:bg-coffee-100 hover:border-coffee-400'
                          }`}
                        >
                          <SocialIcon name={preset.icon} className={`w-4 h-4 ${preset.title === 'Custom Form' ? 'text-accent-700' : 'text-coffee-600'}`} />
                          <span className={`text-xs truncate w-full text-center font-medium ${preset.title === 'Custom Form' ? 'text-accent-700' : 'text-coffee-700'}`}>
                            {preset.title}
                          </span>
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowAllPresets((s) => !s)}
                      className="w-full py-2.5 text-xs font-mono uppercase tracking-wider text-coffee-600 hover:text-coffee-900 border border-dashed border-coffee-200 rounded-lg hover:border-coffee-400 transition-colors"
                    >
                      {showAllPresets
                        ? 'Hide catalog ↑'
                        : `Show ${LINK_PRESETS.length - POPULAR_PRESET_TITLES.length}+ more apps ↓`}
                    </button>

                    {showAllPresets && (
                      <div className="mt-5 space-y-5">
                        {LINK_CATEGORIES.filter((c) => c !== 'All').map((category) => {
                          const presets = LINK_PRESETS.filter((p) => p.category === category);
                          if (presets.length === 0 && category !== 'Content') return null;
                          return (
                            <div key={category}>
                              <div className="flex items-center mb-2">
                                <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500">{category}</p>
                                <div className="flex-1 h-px bg-coffee-200 ml-2" />
                              </div>
                              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                {presets.map((preset) => (
                                  <button
                                    key={`${preset.category}-${preset.title}`}
                                    type="button"
                                    onClick={() => addPresetLink(preset)}
                                    className="bg-coffee-50 border border-coffee-200 rounded-lg p-2.5 text-sm hover:bg-coffee-100 hover:border-coffee-400 transition-colors flex flex-col items-center gap-1.5"
                                  >
                                    <SocialIcon name={preset.icon} className="w-4 h-4 text-coffee-600" />
                                    <span className="text-coffee-700 text-xs truncate w-full text-center font-medium">
                                      {preset.title}
                                    </span>
                                  </button>
                                ))}
                                {category === 'Content' && (
                                  <label className="bg-coffee-50 border border-coffee-200 rounded-lg p-2.5 text-sm hover:bg-coffee-100 hover:border-coffee-400 transition-colors flex flex-col items-center gap-1.5 cursor-pointer">
                                    <SocialIcon name="link" className="w-4 h-4 text-coffee-600" />
                                    <span className="text-coffee-700 text-xs truncate w-full text-center font-medium">
                                      {resumeFilename ? 'Resume ✓' : 'Upload Resume'}
                                    </span>
                                    <input
                                      type="file"
                                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        if (file.size > 5 * 1024 * 1024) { addToast('Resume must be under 5MB', 'error'); return; }
                                        const reader = new FileReader();
                                        reader.onload = (ev) => { setResumeFile(ev.target?.result as string); setResumeFilename(file.name); setResumeType(file.type); addToast('Resume uploaded — save your card to apply', 'success'); };
                                        reader.readAsDataURL(file);
                                      }}
                                      className="hidden"
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 mb-2">
                      {filteredPresets.length} {filteredPresets.length === 1 ? 'result' : 'results'}
                    </p>
                    {filteredPresets.length > 0 ? (
                      <div className="border border-coffee-200 rounded-lg overflow-hidden divide-y divide-coffee-200">
                        {filteredPresets.map((preset, i) => (
                          <button
                            key={`${preset.category}-${preset.title}`}
                            type="button"
                            onClick={() => addPresetLink(preset)}
                            className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                              i === 0 ? 'bg-accent-50 hover:bg-accent-100' : 'bg-white hover:bg-coffee-50'
                            }`}
                          >
                            <div className={`w-9 h-9 rounded flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-accent-500 text-white' : 'bg-coffee-100 text-coffee-700'}`}>
                              <SocialIcon name={preset.icon} className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-coffee-900 truncate">{preset.title}</p>
                              <p className="text-xs text-coffee-600 truncate">
                                {preset.category}
                                {preset.url ? ` · ${preset.url.replace(/^https?:\/\//, '')}` : ''}
                              </p>
                            </div>
                            {i === 0 && (
                              <span className="hidden sm:inline-block text-[10px] font-mono uppercase tracking-wider text-accent-700 flex-shrink-0">↵ Add</span>
                            )}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddLinkForm(true);
                            setNewLink({
                              title: linkSearch,
                              url: 'https://',
                              description: '',
                              icon: 'link',
                              category: 'Content',
                              linkType: 'url',
                              formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
                            });
                            setLinkSearch('');
                          }}
                          className="w-full flex items-center gap-3 p-3 text-left bg-white hover:bg-coffee-50 transition-colors"
                        >
                          <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0 bg-coffee-100 text-coffee-700">
                            <Plus className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-coffee-900">Custom link…</p>
                            <p className="text-xs text-coffee-600 truncate">Use "{linkSearch}" as the title</p>
                          </div>
                        </button>
                      </div>
                    ) : (
                      <div className="border border-coffee-200 rounded-lg p-6 text-center bg-coffee-50">
                        <p className="text-sm text-coffee-600 mb-3">No presets match "{linkSearch}".</p>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddLinkForm(true);
                            setNewLink({
                              title: linkSearch,
                              url: 'https://',
                              description: '',
                              icon: 'link',
                              category: 'Content',
                              linkType: 'url',
                              formConfig: { ...EMPTY_FORM_CONFIG, customFields: [] },
                            });
                            setLinkSearch('');
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-coffee-900 text-cream-50 rounded text-sm font-medium hover:bg-coffee-800"
                        >
                          <Plus className="w-3 h-3" /> Add as custom link
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Add Link Form */}
            {showAddLinkForm && (
              <div className="bg-coffee-50 rounded-xl p-6 mb-6 border border-coffee-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-coffee-900">Add New Link</h4>
                  {/* Read-only type indicator. The link type is fixed by which
                      tile in the picker opened this form — toggling it here
                      stranded users on URL-link with an empty URL (HARN-2). */}
                  <span className="font-mono text-[10px] uppercase tracking-wider text-coffee-500 bg-white border border-coffee-200 rounded px-2 py-0.5">
                    {newLink.linkType === 'contact_form' ? 'Custom Form' : 'URL link'}
                  </span>
                </div>
                <form onSubmit={handleAddLink} className="space-y-4">
                  {newLink.linkType === 'url' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-coffee-700 mb-2">Link Title *</label>
                        <input
                          type="text"
                          value={newLink.title}
                          onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="e.g., LinkedIn Profile"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-coffee-700 mb-2">URL *</label>
                        <input
                          type="url"
                          value={newLink.url}
                          onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="https://linkedin.com/in/username"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-coffee-700 mb-2">Icon</label>
                        <select
                          value={newLink.icon}
                          onChange={(e) => setNewLink({ ...newLink, icon: e.target.value })}
                          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                        >
                          <option value="link">Link</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="twitter">Twitter / X</option>
                          <option value="github">GitHub</option>
                          <option value="instagram">Instagram</option>
                          <option value="facebook">Facebook</option>
                          <option value="youtube">YouTube</option>
                          <option value="tiktok">TikTok</option>
                          <option value="globe">Website</option>
                          <option value="mail">Email</option>
                          <option value="phone">Phone</option>
                          <option value="calendar">Calendar</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-coffee-700 mb-2">Category</label>
                        <div className="flex gap-2">
                          <select
                            value={customCategories.includes(newLink.category) ? newLink.category :
                                   LINK_CATEGORIES.includes(newLink.category) ? newLink.category : '__custom'}
                            onChange={(e) => {
                              if (e.target.value === '__custom') {
                                setNewLink({ ...newLink, category: '' });
                              } else {
                                setNewLink({ ...newLink, category: e.target.value });
                              }
                            }}
                            className="flex-1 px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500"
                          >
                            {LINK_CATEGORIES.filter(c => c !== "All").map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                            {customCategories.filter(c => !LINK_CATEGORIES.includes(c)).map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                            <option value="__custom">+ New category</option>
                          </select>
                        </div>
                        {(!LINK_CATEGORIES.includes(newLink.category) && !customCategories.includes(newLink.category)) && (
                          <input
                            type="text"
                            value={newLink.category}
                            onChange={(e) => setNewLink({ ...newLink, category: e.target.value })}
                            className="mt-2 w-full px-4 py-2 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500"
                            placeholder="Enter custom category name"
                          />
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-coffee-700 mb-2">Description</label>
                        <input
                          type="text"
                          value={newLink.description}
                          onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                          className="w-full px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500"
                          placeholder="Brief description of the link"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-coffee-700 mb-2">Category</label>
                        <select
                          value={customCategories.includes(newLink.category) ? newLink.category :
                                 LINK_CATEGORIES.includes(newLink.category) ? newLink.category : 'Contact'}
                          onChange={(e) => setNewLink({ ...newLink, category: e.target.value })}
                          className="w-full md:w-1/2 px-4 py-3 border border-coffee-200 rounded-lg focus:ring-2 focus:ring-coffee-500"
                        >
                          {LINK_CATEGORIES.filter(c => c !== "All").map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                          {customCategories.filter(c => !LINK_CATEGORIES.includes(c)).map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <ContactFormConfigEditor
                        value={newLink.formConfig}
                        onChange={(next) => setNewLink({ ...newLink, formConfig: next })}
                        isPaidUser={isPaidUser}
                      />
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button type="submit" variant="primary" size="sm">
                      Add link
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddLinkForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Links list grouped by category */}
            {links.length > 0 ? (
              <div className="space-y-3">
                {[...LINK_CATEGORIES.filter(c => c !== "All"), ...customCategories.filter(c => !LINK_CATEGORIES.includes(c))].filter((cat) =>
                  links.some((l) => (l.category || "Content") === cat)
                ).map((category) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold text-coffee-500 uppercase tracking-wider mb-2 mt-4">
                      {category}
                    </h4>
                    {links
                      .filter((l) => (l.category || "Content") === category)
                      .map((link) => {
                        const globalIndex = links.indexOf(link);
                        return (
                          <div
                            key={link.id}
                            className="group bg-coffee-50 rounded-lg p-4 border border-coffee-200 mb-2"
                          >
                            {editingLink?.id === link.id ? (
                              /* ---- Inline edit form ---- */
                              <form onSubmit={handleUpdateLink} className="space-y-4">
                                {editingLink.linkType === 'contact_form' ? (
                                  <div className="space-y-4">
                                    <div>
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">Category</label>
                                      <select
                                        value={customCategories.includes(editingLink.category || '') ? (editingLink.category || 'Contact') :
                                               LINK_CATEGORIES.includes(editingLink.category || '') ? (editingLink.category || 'Contact') : 'Contact'}
                                        onChange={(e) => setEditingLink({ ...editingLink, category: e.target.value })}
                                        className="w-full md:w-1/2 px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                      >
                                        {LINK_CATEGORIES.filter(c => c !== "All").map((cat) => (
                                          <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                        {customCategories.filter(c => !LINK_CATEGORIES.includes(c)).map((cat) => (
                                          <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <ContactFormConfigEditor
                                      value={editingLink.formConfig ?? EMPTY_FORM_CONFIG}
                                      onChange={(next) => setEditingLink({ ...editingLink, formConfig: next })}
                                      isPaidUser={isPaidUser}
                                    />
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">
                                        Title *
                                      </label>
                                      <input
                                        type="text"
                                        value={editingLink.title}
                                        onChange={(e) =>
                                          setEditingLink({ ...editingLink, title: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                        required
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">
                                        URL *
                                      </label>
                                      <input
                                        type="url"
                                        value={editingLink.url}
                                        onChange={(e) =>
                                          setEditingLink({ ...editingLink, url: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                        required
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">
                                        Icon
                                      </label>
                                      <select
                                        value={editingLink.icon || "link"}
                                        onChange={(e) =>
                                          setEditingLink({ ...editingLink, icon: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                      >
                                        <option value="link">Link</option>
                                        <option value="linkedin">LinkedIn</option>
                                        <option value="twitter">Twitter / X</option>
                                        <option value="github">GitHub</option>
                                        <option value="instagram">Instagram</option>
                                        <option value="facebook">Facebook</option>
                                        <option value="youtube">YouTube</option>
                                        <option value="tiktok">TikTok</option>
                                        <option value="globe">Website</option>
                                        <option value="mail">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="calendar">Calendar</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">
                                        Category
                                      </label>
                                      <div className="flex gap-2">
                                        <select
                                          value={customCategories.includes(editingLink.category || '') ? editingLink.category :
                                                 LINK_CATEGORIES.includes(editingLink.category || 'Other') ? (editingLink.category || 'Other') : '__custom'}
                                          onChange={(e) => {
                                            if (e.target.value === '__custom') {
                                              setEditingLink({ ...editingLink, category: '' });
                                            } else {
                                              setEditingLink({ ...editingLink, category: e.target.value });
                                            }
                                          }}
                                          className="flex-1 px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                        >
                                          {LINK_CATEGORIES.filter(c => c !== "All").map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                          ))}
                                          {customCategories.filter(c => !LINK_CATEGORIES.includes(c)).map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                          ))}
                                          <option value="__custom">+ New category</option>
                                        </select>
                                      </div>
                                      {(!LINK_CATEGORIES.includes(editingLink.category || '') && !customCategories.includes(editingLink.category || '')) && (
                                        <input
                                          type="text"
                                          value={editingLink.category || ''}
                                          onChange={(e) => setEditingLink({ ...editingLink, category: e.target.value })}
                                          className="mt-2 w-full px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                          placeholder="Enter custom category name"
                                        />
                                      )}
                                    </div>
                                    <div className="md:col-span-2">
                                      <label className="block text-sm font-medium text-coffee-700 mb-2">
                                        Description
                                      </label>
                                      <input
                                        type="text"
                                        value={editingLink.description || ""}
                                        onChange={(e) =>
                                          setEditingLink({
                                            ...editingLink,
                                            description: e.target.value,
                                          })
                                        }
                                        className="w-full px-3 py-2 border border-coffee-200 rounded focus:ring-2 focus:ring-coffee-500"
                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="flex gap-3">
                                  <Button type="submit" variant="primary" size="sm">
                                    Save
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingLink(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </form>
                            ) : (
                              /* ---- Display mode ---- */
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 min-w-0">
                                  <div className="w-10 h-10 bg-coffee-100 rounded-full flex items-center justify-center flex-shrink-0 text-coffee-600">
                                    {link.customIcon ? (
                                      <img src={link.customIcon} alt="" className="w-5 h-5 rounded object-cover" />
                                    ) : (
                                      <SocialIcon name={link.icon || "link"} className="w-5 h-5" />
                                    )}
                                  </div>
                                  {isPaidUser && (
                                    <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity ml-1" title="Custom icon (Pro)">
                                      <svg className="w-3 h-3 text-coffee-400 hover:text-coffee-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          if (file.size > 500 * 1024) { addToast('Icon must be under 500KB', 'error'); return; }
                                          const reader = new FileReader();
                                          reader.onload = async (ev) => {
                                            const customIcon = ev.target?.result as string;
                                            try {
                                              await updateCardLink(link.id, { customIcon });
                                              setLinks(prev => prev.map(l => l.id === link.id ? { ...l, customIcon } : l));
                                              addToast('Icon updated', 'success');
                                            } catch {
                                              addToast('Failed to update icon', 'error');
                                            }
                                          };
                                          reader.readAsDataURL(file);
                                        }}
                                      />
                                    </label>
                                  )}
                                  <div className="min-w-0">
                                    <h4 className="font-semibold text-coffee-900">
                                      {link.title}{" "}
                                      <span className="text-xs bg-coffee-200 text-coffee-700 px-2 py-0.5 rounded-full">
                                        {linkClicks[link.id] || 0} clicks
                                      </span>
                                    </h4>
                                    <p className="text-sm text-coffee-600 truncate">{link.url}</p>
                                    {link.description && (
                                      <p className="text-xs text-coffee-500">{link.description}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                  <div className="flex flex-col gap-1">
                                    <button
                                      onClick={() => moveLink(link.id, "up")}
                                      disabled={globalIndex === 0}
                                      className="p-1.5 rounded hover:bg-coffee-100 disabled:opacity-30 transition-colors"
                                      title="Move up"
                                    >
                                      <svg className="w-4 h-4 text-coffee-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => moveLink(link.id, "down")}
                                      disabled={globalIndex === links.length - 1}
                                      className="p-1.5 rounded hover:bg-coffee-100 disabled:opacity-30 transition-colors"
                                      title="Move down"
                                    >
                                      <svg className="w-4 h-4 text-coffee-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                  </div>
                                  <button
                                    onClick={() => {
                                      fetchLinkPreview(link.url).then(async (preview) => {
                                        if (preview.title || preview.image || preview.description) {
                                          try {
                                            await updateCardLink(link.id, {
                                              ogTitle: preview.title || undefined,
                                              ogDescription: preview.description || undefined,
                                              ogImage: preview.image || undefined,
                                              ogSiteName: preview.siteName || undefined,
                                            });
                                            setLinks(prev => prev.map(l =>
                                              l.id === link.id
                                                ? { ...l, ogTitle: preview.title, ogDescription: preview.description, ogImage: preview.image, ogSiteName: preview.siteName } as any
                                                : l
                                            ));
                                            addToast("Preview refreshed!", "success");
                                          } catch {
                                            addToast("Failed to update preview.", "error");
                                          }
                                        } else {
                                          addToast("No preview data found.", "error");
                                        }
                                      }).catch(() => {
                                        addToast("Failed to fetch preview.", "error");
                                      });
                                    }}
                                    className="p-2 text-coffee-600 hover:text-coffee-800 rounded hover:bg-coffee-100 transition-colors"
                                    title="Refresh preview"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingLink(link)}
                                    className="p-2 text-coffee-600 hover:text-coffee-800 rounded hover:bg-coffee-100 transition-colors"
                                    title="Edit"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLink(link.id)}
                                    className="p-2 text-red-600 hover:text-red-800 rounded hover:bg-red-50 transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <div className="w-12 h-12 rounded-xl bg-coffee-50 border border-coffee-100 flex items-center justify-center mx-auto mb-4">
                  <LinkIcon className="w-5 h-5 text-coffee-400" />
                </div>
                <h3 className="font-semibold text-coffee-900 mb-1">No links yet</h3>
                <p className="text-sm text-coffee-600 max-w-xs mx-auto mb-4">
                  Add a link to get started. LinkedIn, your portfolio, or a booking calendar are good first choices.
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowAddLinkForm(true)}
                >
                  Add your first link
                </Button>
              </div>
            )}
          </div>
          )}

            {/* Theme picker moved into the three-pane Edit my Card layout
                (see THEME_OPTIONS + the editing block above). The global Save
                button persists the selectedTheme alongside the rest of the
                card metadata — no separate Apply step. */}
          </div>

          {/* Sidebar - QR + link (compact, sticky) */}
          <div className="order-2">
            <div className="lg:sticky lg:top-20 space-y-4">
              <div className="bg-white rounded-2xl shadow-coffee-lg p-4">
                <div>
                  <p className="text-xs text-coffee-500 mb-2">Share your card</p>
                  {hasHandle ? (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(cardUrl);
                        addToast('Link copied to clipboard', 'success');
                      }}
                      className="inline-flex items-center gap-2 text-coffee-700 hover:text-coffee-900 text-sm font-medium transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      Copy link
                    </button>
                  ) : (
                    <p className="text-xs text-coffee-600">
                      Set a handle in your{" "}
                      <a href="/profile" className="underline font-medium text-coffee-800 hover:text-coffee-900">Profile</a>{" "}
                      to get a shareable link.
                    </p>
                  )}
                </div>

                {/* QR Code — only meaningful once the user has a public handle. */}
                {hasHandle && (
                <div className="flex flex-col items-center gap-3 mt-4">
                  <div className="bg-white border border-coffee-200 rounded-lg p-2">
                    <img
                      src={qrUrl}
                      alt={`QR code for ${handle}`}
                      className="w-28 h-28"
                      loading="lazy"
                    />
                  </div>
                  {isPaidUser && card && (
                    <div className="mt-3 flex items-center gap-3 justify-center">
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-coffee-500">Code</label>
                        <input
                          type="color"
                          value={`#${qrFgColor}`}
                          onChange={(e) => setQrFgColor(e.target.value.replace('#', ''))}
                          className="w-7 h-7 rounded cursor-pointer border border-coffee-200"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-coffee-500">BG</label>
                        <input
                          type="color"
                          value={`#${qrBgColor}`}
                          onChange={(e) => setQrBgColor(e.target.value.replace('#', ''))}
                          className="w-7 h-7 rounded cursor-pointer border border-coffee-200"
                        />
                      </div>
                    </div>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    onClick={downloadQRCode}
                  >
                    Download QR
                  </Button>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Sticky save bar at bottom */}
        {card && (
            <div className="sticky bottom-0 bg-white border-t border-coffee-100 px-4 py-3 -mx-4 mt-8 flex items-center justify-between z-20">
                <p className="text-sm text-coffee-500 hidden sm:block">Unsaved changes are not applied until you save.</p>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(false)}
                    >
                        Discard
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSaveCard()}
                        loading={savingProfile}
                    >
                        {savingProfile ? 'Saving' : 'Save'}
                    </Button>
                </div>
            </div>
        )}

        </>)}

        {/* ============================================================ */}
        {/*  Carry your card — wallet passes + home screen (D-007)       */}
        {/* ============================================================ */}
        {card && (
          <WalletSection
            cardId={card.id}
            card={{
              name: card.name,
              position: card.position,
              company: card.company,
              profilePhoto: card.profilePhoto,
              squareLogo: card.squareLogo,
              theme: card.theme,
              showProBadge: card.showProBadge,
              badgeStyle: card.badgeStyle,
            }}
            publicUrl={cardUrl || null}
            isPro={isPaidUser}
          />
        )}

        {/* ============================================================ */}
        {/*  Order Physical Card CTA                                     */}
        {/* ============================================================ */}
        {dbUser?.hasPhysicalCard ? (
          <div className="bg-coffee-900 rounded-2xl p-6 text-center">
            <div className="eyebrow text-coffee-200 mb-2">Physical card</div>
            <p className="text-coffee-200 text-sm mb-4">
              You have <span className="font-mono">{dbUser.physicalCardCount || 1}</span> physical {dbUser.physicalCardCount === 1 ? 'card' : 'cards'} on order.
            </p>
            <Button variant="secondary" size="sm" to="/order-card">
              Order more
            </Button>
          </div>
        ) : (
          <div className="bg-coffee-900 rounded-2xl p-8 text-white overflow-hidden relative">
            <div className="absolute -top-24 -right-16 w-64 h-64 rounded-full bg-coffee-700 blur-3xl opacity-40" />
            <div className="relative flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="eyebrow text-coffee-200 mb-2">Physical card</div>
                <h3 className="text-display text-2xl text-white mb-2">Add a physical card.</h3>
                <p className="text-coffee-200 text-sm">
                  Tap to share. NFC-enabled, links to your card page. No app needed for recipients.
                </p>
              </div>
              <Button variant="secondary" size="md" to="/order-card">
                Order — $14.99
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MyCard;
